import React, { useState, useEffect, useCallback, memo, useRef, useMemo, startTransition } from 'react';
import ShippingPreparationModal from '../components/ShippingPreparationModal';
import HoverZoomImage from '../components/HoverZoomImage';
import ShopifyOrdersPage from './ShopifyOrdersPage';
import productLinksService from '../services/ProductLinksService'
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  TextField,
  Pagination,
  Stack,
  Chip,
  IconButton,
  Grid,
  Alert,
  Tooltip,
  alpha,
  Skeleton,
  InputAdornment,
  LinearProgress
} from '@mui/material';
import _ from 'lodash';

import {
  Refresh as RefreshIcon,
  LocalShipping as ShippingIcon,
  Print as PrintIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  SortByAlpha as SortIcon,
  LocalShipping as LocalShippingIcon,
  ContentCopy as ContentCopyIcon,
  Close as CloseIcon,
  FilterList as FilterListIcon,
  Search as SearchIcon,
  AccessTime as AccessTimeIcon,
  CalendarToday as CalendarTodayIcon,
  TrendingUp as TrendingUpIcon,
  Inventory2 as Inventory2Icon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  PhotoLibrary as PhotoLibraryIcon,
  ClearAll as ClearAllIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  SignalWifiStatusbar4Bar as ConnectedIcon,
  SignalWifiOff as DisconnectedIcon,
  KeyboardArrowDown as ArrowDownIcon,
  ViewList as ViewListIcon,
  Category as CategoryIcon,
  ShoppingCart as ShoppingCartIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import moment from 'moment';

import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import ExcelLikeField from '../components/ExcelLikeField';
import ProductImageGallery from '../components/ProductImageGallery';
import { PasswordLoginScreen } from '../components/PasswordLoginScreen';
import { io, Socket } from 'socket.io-client';

// ============================================================================
// TYPES & INTERFACES
// ============================================================================

interface OrderItem {
  id: string;
  orderId: string;
  productName: string;
  itemSpec: string;
  quantity: number;
  imageUrl: string;
  sku: string;
  productKey?: string;
  received: boolean;
  missingCount: number;
  referenceImage?: string;
  tag?: string;
  note: string;
  original: any;
  orderLink?: string;
  receivedBy?: string;
  receivedAt?: string;
}

interface ProductInfo {
  link: string;
  allLinks?: string[];
}

interface TrackingChanges {
  received?: boolean;
  missingCount?: number;
  note?: string;
  tag?: string;
  referenceImage?: string;
  receivedBy?: string;
  receivedAt?: string;
}

interface ProductMetadata {
  product_key: string;
  tag: string;
  reference_image?: string;
  referenceImage?: string;
  createdAt: string;
  lastUpdated: string;
}

interface OrderCardProps {
  order: any;
  activeTab: string;
  socket?: Socket | null;
  receivedFilter: string;
  handleShipOrder: (order: any) => void;
  handlePrintShippingDocument: (order: any) => void;
  enqueueSnackbar: (message: string, options?: any) => void;
  handleTrackingUpdate: (orderItem: any, field: string, value: any) => void;
  handleReferenceImageUpload: (event: React.ChangeEvent<HTMLInputElement>, item: any) => void;
  handleRemoveReferenceImage: (item: any) => void;
  handleSkuUpdate: (item: OrderItem, newSku: string) => void;
  setExpandedOrders: React.Dispatch<React.SetStateAction<Set<string>>>;
  setReceivedFilter: (filter: string) => void;
  decodeHtmlEntities: (input: string) => string;
}

interface ExcelProductData {
  id: string;
  link: string;
  allLinks?: string[];
  [key: string]: any;
}

declare global {
  interface Window {
    __trackedItems?: Set<string>;
  }
}

// ============================================================================
// CUSTOM STYLES
// ============================================================================

const styles = {
  // Base container — warm neutral backdrop
  pageContainer: {
    width: '100%',
    height: '100vh',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    bgcolor: '#f5f3f0',
    fontFamily: '"DM Sans", "Noto Sans TC", sans-serif',
  },

  // Header — clean with subtle bottom edge
  header: {
    flexShrink: 0,
    px: { xs: 2, md: 3 },
    pt: { xs: 2, md: 2.5 },
    pb: 2,
    bgcolor: '#ffffff',
    borderBottom: '1px solid #e8e5e1',
    boxShadow: '0 1px 0 rgba(0,0,0,0.03)',
  },

  headerTitle: {
    fontWeight: 800,
    fontSize: { xs: '1.35rem', md: '1.6rem' },
    color: '#1a1a1a',
    letterSpacing: '-0.03em',
    lineHeight: 1.2,
  },

  // Tab toggle — pill style
  tabContainer: {
    bgcolor: '#f0ede9',
    p: '3px',
    borderRadius: '12px',
    display: 'inline-flex',
    gap: '3px',
  },

  tabButton: (active: boolean) => ({
    borderRadius: '10px',
    boxShadow: active ? '0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.06)' : 'none',
    bgcolor: active ? '#ffffff' : 'transparent',
    color: active ? '#1a1a1a' : '#8c8580',
    px: { xs: 2, md: 2.5 },
    py: 0.75,
    fontWeight: active ? 700 : 500,
    textTransform: 'none' as const,
    minWidth: 72,
    fontSize: '0.875rem',
    transition: 'all 0.15s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
      bgcolor: active ? '#ffffff' : 'rgba(0,0,0,0.04)',
      color: active ? '#1a1a1a' : '#5c5854',
    },
  }),

  // Filter panel — inset card feel
  filterPanel: {
    p: { xs: 2, md: 2.5 },
    mb: 0,
    mt: 2,
    border: '1px solid #e8e5e1',
    borderRadius: '14px',
    bgcolor: '#fafaf8',
    boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.02)',
  },

  filterChip: {
    borderRadius: '8px',
    fontWeight: 600,
    fontSize: '0.8rem',
    height: 28,
    '& .MuiChip-deleteIcon': {
      color: 'inherit',
      opacity: 0.6,
      fontSize: 16,
      '&:hover': { opacity: 1 },
    },
  },

  // Order card — elevated with left accent
  orderCard: {
    borderRadius: '16px',
    border: '1px solid #e8e5e1',
    bgcolor: '#ffffff',
    boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.02)',
    overflow: 'visible',
    transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
    '&:hover': {
      boxShadow: '0 4px 12px rgba(0,0,0,0.06), 0 1px 3px rgba(0,0,0,0.04)',
    },
  },

  orderCardReady: {
    borderLeft: '4px solid #22c55e',
    borderColor: '#d1fae5',
  },

  orderCardPartial: {
    borderLeft: '4px solid #f59e0b',
    borderColor: '#fef3c7',
  },

  // Table styles — refined
  tableHeader: {
    bgcolor: '#fafaf8',
    '& .MuiTableCell-head': {
      fontWeight: 700,
      fontSize: '0.75rem',
      textTransform: 'uppercase' as const,
      letterSpacing: '0.06em',
      color: '#8c8580',
      borderBottom: '1px solid #e8e5e1',
      py: 1.5,
      px: 2,
    },
  },

  tableRow: (received: boolean) => ({
    bgcolor: received ? '#f0fdf4' : 'transparent',
    transition: 'background-color 0.15s ease',
    '&:hover': {
      bgcolor: received ? '#dcfce7' : '#fafaf8',
    },
    '& .MuiTableCell-root': {
      borderBottom: '1px solid #f3f1ee',
      py: 2,
      px: 2,
      fontSize: '0.9rem',
    },
  }),

  // Status chips — refined
  statusChip: {
    fontWeight: 700,
    fontSize: '0.7rem',
    height: 26,
    borderRadius: '8px',
    letterSpacing: '0.02em',
  },

  // Connection indicator — minimal dot style
  connectionIndicator: (connected: boolean) => ({
    display: 'flex',
    alignItems: 'center',
    gap: 0.75,
    px: 1.5,
    py: 0.5,
    borderRadius: '10px',
    bgcolor: connected ? alpha('#22c55e', 0.08) : alpha('#ef4444', 0.08),
    color: connected ? '#15803d' : '#b91c1c',
    fontSize: '0.7rem',
    fontWeight: 600,
    letterSpacing: '0.02em',
    border: '1px solid',
    borderColor: connected ? alpha('#22c55e', 0.15) : alpha('#ef4444', 0.15),
  }),
};

// ============================================================================
// SKELETON LOADERS
// ============================================================================

const OrderCardSkeleton: React.FC = () => (
  <Card sx={{ ...styles.orderCard, mb: 2, overflow: 'hidden' }}>
    <CardHeader
      sx={{ pb: 1.5, pt: 2, px: 3 }}
      title={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Skeleton variant="text" width={140} height={28} sx={{ borderRadius: '6px' }} />
          <Skeleton variant="rounded" width={70} height={26} sx={{ borderRadius: '8px' }} />
          <Skeleton variant="rounded" width={90} height={6} sx={{ borderRadius: '3px', ml: 1 }} />
        </Box>
      }
      subheader={
        <Box sx={{ mt: 1.5, display: 'flex', gap: 0.75 }}>
          {[80, 100, 70, 90].map((w, i) => (
            <Skeleton key={i} variant="rounded" width={w} height={22} sx={{ borderRadius: '6px' }} />
          ))}
        </Box>
      }
    />
    <CardContent sx={{ p: 0 }}>
      <TableContainer>
        <Table size="medium">
          <TableHead>
            <TableRow sx={styles.tableHeader}>
              {[90, 180, 120, 70, 120, 140, 70, 80, 100, 70].map((w, i) => (
                <TableCell key={i}><Skeleton variant="text" width={w} height={24} /></TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {[1, 2].map((row) => (
              <TableRow key={row}>
                <TableCell><Skeleton variant="rectangular" width={80} height={80} sx={{ borderRadius: 1.5 }} /></TableCell>
                <TableCell><Skeleton variant="text" width="90%" height={24} /></TableCell>
                <TableCell><Skeleton variant="text" width="70%" height={24} /></TableCell>
                <TableCell><Skeleton variant="text" width={40} height={24} /></TableCell>
                <TableCell><Skeleton variant="text" width="80%" height={24} /></TableCell>
                <TableCell><Skeleton variant="text" width="60%" height={24} /></TableCell>
                <TableCell align="center"><Skeleton variant="circular" width={28} height={28} /></TableCell>
                <TableCell><Skeleton variant="circular" width={48} height={48} /></TableCell>
                <TableCell><Skeleton variant="text" width={80} height={24} /></TableCell>
                <TableCell><Skeleton variant="circular" width={28} height={28} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </CardContent>
  </Card>
);

// ============================================================================
// FILTER SUMMARY COMPONENT
// ============================================================================

const FilterSummary: React.FC<{
  searchFilter: string;
  statusFilter: string;
  colorFilter: string;
  sizeFilter: string;
  receivedFilter: string;
  tagFilter: string;
  onClear: (filter: string) => void;
  onClearAll: () => void;
}> = memo(({ searchFilter, statusFilter, colorFilter, sizeFilter, receivedFilter, tagFilter, onClear, onClearAll }) => {
  const hasFilters = searchFilter || statusFilter !== 'All' || colorFilter !== 'All' ||
    sizeFilter !== 'All' || receivedFilter !== 'All' || tagFilter !== 'All';

  if (!hasFilters) return null;

  const filterLabels: Record<string, string> = {
    search: `搜尋: ${searchFilter}`,
    status: statusFilter === 'OVERDUE_10' ? '超過10天未出貨' : `狀態: ${statusFilter}`,
    color: `顏色: ${colorFilter}`,
    size: `尺寸: ${sizeFilter}`,
    received: receivedFilter === 'Unreceived' ? '未收貨' :
      receivedFilter === 'Received' ? '已收貨' :
        receivedFilter === 'ReadyToShip' ? '可出貨' : receivedFilter,
    tag: tagFilter === 'NumbersOnly' ? '僅數字標籤' :
      tagFilter === 'NoNumbers' ? '非數字標籤' :
        tagFilter === 'NoTag' ? '無標籤' : `標籤: ${tagFilter}`,
  };

  return (
    <Box sx={{
      display: 'flex',
      flexWrap: 'wrap',
      gap: 0.75,
      mt: 2,
      pt: 1.5,
      borderTop: '1px solid #e8e5e1',
      alignItems: 'center',
    }}>
      <Typography sx={{ fontSize: '0.7rem', color: '#a39e99', fontWeight: 600, mr: 0.5, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
        篩選
      </Typography>

      {searchFilter && (
        <Chip
          label={filterLabels.search}
          onDelete={() => onClear('search')}
          size="small"
          color="primary"
          sx={styles.filterChip}
        />
      )}
      {statusFilter !== 'All' && (
        <Chip
          label={filterLabels.status}
          onDelete={() => onClear('status')}
          size="small"
          sx={styles.filterChip}
        />
      )}
      {colorFilter !== 'All' && (
        <Chip
          label={filterLabels.color}
          onDelete={() => onClear('color')}
          size="small"
          sx={styles.filterChip}
        />
      )}
      {sizeFilter !== 'All' && (
        <Chip
          label={filterLabels.size}
          onDelete={() => onClear('size')}
          size="small"
          sx={styles.filterChip}
        />
      )}
      {receivedFilter !== 'All' && (
        <Chip
          label={filterLabels.received}
          onDelete={() => onClear('received')}
          size="small"
          color="warning"
          sx={styles.filterChip}
        />
      )}
      {tagFilter !== 'All' && (
        <Chip
          label={filterLabels.tag}
          onDelete={() => onClear('tag')}
          size="small"
          sx={styles.filterChip}
        />
      )}

      <Button
        size="small"
        startIcon={<ClearAllIcon sx={{ fontSize: 14 }} />}
        onClick={onClearAll}
        sx={{ ml: 'auto', textTransform: 'none', fontSize: '0.75rem', color: '#8c8580', fontWeight: 600 }}
      >
        清除
      </Button>
    </Box>
  );
});



// ============================================================================
// ORDER CARD COMPONENT
// ============================================================================

const OrderCard = memo<OrderCardProps>(({
  order,
  activeTab,
  socket,
  receivedFilter,
  handleShipOrder,
  handlePrintShippingDocument,
  enqueueSnackbar,
  handleTrackingUpdate,
  handleReferenceImageUpload,
  handleRemoveReferenceImage,
  handleSkuUpdate,
  setExpandedOrders,
  setReceivedFilter,
  decodeHtmlEntities,
}) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const [showGallery, setShowGallery] = useState(false);
  const [productLinks, setProductLinks] = useState<{ [key: string]: ProductInfo }>({});
  const [loadingLinks, setLoadingLinks] = useState(false);


  const toggleGallery = useCallback(() => {
    setShowGallery(prev => !prev);
  }, []);

  // Fetch product links - OPTIMIZED with cache
  useEffect(() => {
    let cancelled = false;

    const fetchProductLinks = async () => {
      if (!order.items || order.items.length === 0) return;

      const itemsWithSku = order.items.filter((item: OrderItem) => item.sku);
      if (itemsWithSku.length === 0) return;

      // Check cache first
      const cacheKey = `productLinks_${order.orderId}`;
      const cached = sessionStorage.getItem(cacheKey);
      if (cached) {
        try {
          setProductLinks(JSON.parse(cached));
          return;
        } catch (e) { /* ignore */ }
      }

      setLoadingLinks(true);
      try {
        const storedExcelData = localStorage.getItem('productLinksData');
        let productsList: ExcelProductData[] = [];

        if (storedExcelData) {
          productsList = JSON.parse(storedExcelData);
        } else {
          productsList = await productLinksService.fetchProductLinks() as ExcelProductData[];
        }

        if (cancelled) return;

        const productInfoMap: { [key: string]: ProductInfo } = {};

        order.items.forEach((item: OrderItem) => {
          if (!item.sku) return;
          const matchingProduct = productsList.find(p => p.id === item.sku);

          if (matchingProduct) {
            // Check if the product has multiple links
            if (matchingProduct.allLinks && Array.isArray(matchingProduct.allLinks) && matchingProduct.allLinks.length > 0) {
              productInfoMap[item.id] = {
                link: decodeHtmlEntities(matchingProduct.link || matchingProduct.allLinks[0] || ''),
                allLinks: matchingProduct.allLinks.map((link: string) => decodeHtmlEntities(link))
              };
            } else if (matchingProduct.link) {
              productInfoMap[item.id] = {
                link: decodeHtmlEntities(matchingProduct.link)
              };
            }
          }
        });

        if (!cancelled) {
          setProductLinks(productInfoMap);
          // Cache for this session
          sessionStorage.setItem(cacheKey, JSON.stringify(productInfoMap));
        }
      } catch (error) {
        console.error('獲取產品連結時出錯:', error);
      } finally {
        if (!cancelled) setLoadingLinks(false);
      }
    };

    fetchProductLinks();

    return () => { cancelled = true; };
  }, [order.orderId, order.items?.length]);

  const isReadyToShip = order.readyToShip && order.status !== 'SHIPPED' && order.status !== 'COMPLETED';

  return (
    <Card
      ref={cardRef}
      elevation={0}
      sx={{
        ...styles.orderCard,
        ...(order.allItemsReceived ? styles.orderCardReady :
          order.receivedStatus === 'partial' ? styles.orderCardPartial : {}),
        minHeight: 'fit-content',
      }}
    >
      {/* Card Header */}
      <CardHeader
        sx={{
          pb: 1.5,
          pt: 2,
          px: { xs: 2, md: 3 },
          '& .MuiCardHeader-content': { width: '100%' },
        }}
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 800,
                  color: '#1a1a1a',
                  fontSize: '1.1rem',
                  fontFamily: '"DM Mono", "SF Mono", monospace',
                  letterSpacing: '-0.02em',
                }}
              >
                #{order.orderNumber}
              </Typography>

              <Stack direction="row" spacing={1}>
                {order.readyToShip && (
                  <Chip
                    icon={<LocalShippingIcon sx={{ fontSize: 18 }} />}
                    label="可出貨"
                    color="success"
                    size="medium"
                    sx={{ ...styles.statusChip, fontWeight: 700, height: 32, fontSize: '0.875rem' }}
                  />
                )}

                {order.receivedStatus === 'complete' && !order.readyToShip && (
                  <Chip
                    icon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
                    label="全部已收"
                    color="success"
                    size="medium"
                    variant="outlined"
                    sx={{ ...styles.statusChip, height: 32, fontSize: '0.875rem' }}
                  />
                )}
                {order.receivedStatus === 'partial' && (
                  <Chip
                    icon={<WarningIcon sx={{ fontSize: 16 }} />}
                    label={`${order.receivedItemsInOrder}/${order.totalItemsInOrder}`}
                    color="warning"
                    size="medium"
                    variant="outlined"
                    sx={{ ...styles.statusChip, height: 32, fontSize: '0.875rem' }}
                  />
                )}
                {order.receivedStatus === 'pending' && (
                  <Chip
                    label="未收"
                    color="error"
                    size="medium"
                    variant="outlined"
                    sx={{ ...styles.statusChip, height: 32, fontSize: '0.875rem' }}
                  />
                )}

                {order.isFiltered && (
                  <Chip
                    label={`${order.filteredItemCount}/${order.originalItemCount}`}
                    variant="outlined"
                    size="medium"
                    color="info"
                    sx={{ ...styles.statusChip, height: 32, fontSize: '0.875rem' }}
                  />
                )}
              </Stack>

              {/* Progress Bar */}
              {order.totalItemsInOrder > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 120 }}>
                  <LinearProgress
                    variant="determinate"
                    value={(order.receivedItemsInOrder / order.totalItemsInOrder) * 100}
                    color={order.allItemsReceived ? 'success' : order.receivedStatus === 'partial' ? 'warning' : 'error'}
                    sx={{
                      flex: 1, height: 6, borderRadius: 3,
                      bgcolor: '#f0ede9',
                      '& .MuiLinearProgress-bar': {
                        borderRadius: 3,
                        transition: 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                      },
                    }}
                  />
                  <Typography variant="caption" sx={{ whiteSpace: 'nowrap', fontWeight: 700, fontSize: '0.7rem', color: '#5c5854', fontFamily: '"DM Mono", monospace' }}>
                    {order.receivedItemsInOrder}/{order.totalItemsInOrder}
                  </Typography>
                </Box>
              )}
            </Box>

            <Stack direction="row" spacing={1}>
              {activeTab === 'shopee' && (
                <>
                  {isReadyToShip && (
                    <Button
                      variant="contained"
                      startIcon={<ShippingIcon sx={{ fontSize: 16 }} />}
                      onClick={() => handleShipOrder(order)}
                      sx={{
                        fontWeight: 700,
                        boxShadow: 'none',
                        textTransform: 'none',
                        borderRadius: '10px',
                        px: 2,
                        py: 0.75,
                        fontSize: '0.8rem',
                        bgcolor: '#22c55e',
                        '&:hover': { bgcolor: '#16a34a', boxShadow: '0 2px 8px rgba(34,197,94,0.3)' },
                      }}
                    >
                      出貨
                    </Button>
                  )}
                  {!isReadyToShip && order.status !== 'SHIPPED' && order.status !== 'COMPLETED' && (
                    <Button
                      variant="outlined"
                      startIcon={<ShippingIcon sx={{ fontSize: 16 }} />}
                      onClick={() => handleShipOrder(order)}
                      disabled={!order.allItemsReceived}
                      sx={{ textTransform: 'none', borderRadius: '10px', px: 1.5, py: 0.5, fontSize: '0.8rem', borderColor: '#e8e5e1', color: '#5c5854' }}
                    >
                      出貨
                    </Button>
                  )}
                  <Tooltip title="列印出貨單">
                    <IconButton
                      onClick={() => handlePrintShippingDocument(order)}
                      sx={{
                        border: '1px solid #e8e5e1',
                        borderRadius: '10px',
                        width: 34,
                        height: 34,
                        color: '#8c8580',
                        '&:hover': { bgcolor: '#f5f3f0', color: '#1a1a1a' },
                      }}
                    >
                      <PrintIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                </>
              )}

              <Button
                variant="text"
                onClick={toggleGallery}
                startIcon={showGallery ? <VisibilityOffIcon sx={{ fontSize: 16 }} /> : <PhotoLibraryIcon sx={{ fontSize: 16 }} />}
                sx={{ textTransform: 'none', color: '#8c8580', fontSize: '0.8rem', '&:hover': { color: '#1a1a1a' } }}
              >
                {showGallery ? '隱藏' : '相簿'}
              </Button>

              {(() => {
                const itemsWithLinks = order.items.filter((item: OrderItem) =>
                  productLinks[item.id]?.link
                );
                if (itemsWithLinks.length > 0) {
                  return (
                    <Button
                      variant="text"
                      startIcon={<OpenInNewIcon />}
                      onClick={() => {
                        itemsWithLinks.forEach((item: OrderItem, idx: number) => {
                          const link = productLinks[item.id]?.link;
                          if (link) {
                            setTimeout(() => {
                              window.open(link, '_blank');
                            }, idx * 100);
                          }
                        });
                        enqueueSnackbar(`開啟 ${itemsWithLinks.length} 個連結`, { variant: 'info' });
                      }}
                      sx={{ textTransform: 'none', color: 'text.secondary', fontSize: '0.9rem' }}
                    >
                      連結 ({itemsWithLinks.length})
                    </Button>
                  );
                }
                return null;
              })()}
            </Stack>
          </Box>
        }
        subheader={
          <Box sx={{ mt: 1.5 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center' }}>
              {[
                { icon: <CalendarTodayIcon sx={{ fontSize: 13 }} />, text: order.createdAt },
                {
                  icon: <AccessTimeIcon sx={{ fontSize: 13 }} />,
                  text: `截止 ${order.deadline}`,
                  urgent: moment(order.deadline).diff(moment(), 'days') < 2,
                },
                { icon: <TrendingUpIcon sx={{ fontSize: 13 }} />, text: ({ READY_TO_SHIP: '未出貨', PROCESSED: '已按出貨', SHIPPED: '已出貨', COMPLETED: '已完成' } as Record<string, string>)[order.status] || order.status.replace(/_/g, ' ').toLowerCase() },
                { icon: <LocalShippingIcon sx={{ fontSize: 13 }} />, text: order.shippingCarrier || '標準運送' },
              ].map((meta, idx) => (
                <Box key={idx} sx={{
                  display: 'inline-flex', alignItems: 'center', gap: 0.5,
                  px: 1, py: 0.25,
                  borderRadius: '6px',
                  bgcolor: (meta as any).urgent ? '#fef2f2' : '#f5f3f0',
                  color: (meta as any).urgent ? '#b91c1c' : '#8c8580',
                  fontSize: '0.75rem',
                  fontWeight: (meta as any).urgent ? 700 : 500,
                  textTransform: 'capitalize' as const,
                }}>
                  {meta.icon}
                  {meta.text}
                </Box>
              ))}

              {order.trackingNumber && (
                <Box
                  onClick={() => {
                    navigator.clipboard.writeText(order.trackingNumber);
                    enqueueSnackbar('已複製追蹤號碼', { variant: 'success' });
                  }}
                  sx={{
                    display: 'inline-flex', alignItems: 'center', gap: 0.5,
                    px: 1.25, py: 0.25,
                    borderRadius: '6px',
                    bgcolor: '#eff6ff',
                    color: '#1d4ed8',
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    fontFamily: '"DM Mono", monospace',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                    '&:hover': { bgcolor: '#dbeafe', transform: 'translateY(-1px)' },
                  }}
                >
                  <ContentCopyIcon sx={{ fontSize: 12 }} />
                  {order.trackingNumber}
                </Box>
              )}
            </Box>
          </Box>
        }
      />

      {/* Ready to Ship Banner */}
      {order.allItemsReceived && order.status !== 'SHIPPED' && order.status !== 'COMPLETED' && (
        <Box sx={{
          mx: { xs: 1.5, md: 2.5 },
          mb: 1.5,
          px: 2, py: 1.25,
          bgcolor: '#f0fdf4',
          border: '1px solid #bbf7d0',
          borderRadius: '10px',
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          animation: 'fadeSlideIn 0.3s ease-out',
          '@keyframes fadeSlideIn': {
            '0%': { opacity: 0, transform: 'translateY(-4px)' },
            '100%': { opacity: 1, transform: 'translateY(0)' },
          },
        }}>
          <Box sx={{
            width: 28, height: 28, borderRadius: '8px',
            bgcolor: '#22c55e', display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <LocalShippingIcon sx={{ fontSize: 16, color: '#ffffff' }} />
          </Box>
          <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: '#15803d' }}>
            全部商品已收到 - 可以出貨
          </Typography>
        </Box>
      )}

      {/* Enhanced Filter Alert */}
      {order.isFiltered && (() => {
        const totalItems = order.originalItemCount || order.totalItemsInOrder || 0;
        const receivedItems = order.receivedItemsInOrder || 0;
        const remainingUnreceived = totalItems - receivedItems;
        return (
          <Alert
            severity="warning"
            icon={<WarningIcon />}
            sx={{
              mx: 2,
              mb: 1.5,
              py: 0.5,
              borderRadius: 2,
              '& .MuiAlert-message': { py: 0.5 },
            }}
            action={
              <Button
                color="inherit"
                size="small"
                endIcon={order.isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                onClick={() => {
                  setExpandedOrders(prev => {
                    const newSet = new Set(prev);
                    if (newSet.has(order.orderId)) {
                      newSet.delete(order.orderId);
                    } else {
                      newSet.add(order.orderId);
                    }
                    return newSet;
                  });
                }}
                sx={{ textTransform: 'none' }}
              >
                {order.isExpanded ? '僅顯示搜尋結果' : '顯示全部商品'}
              </Button>
            }
          >
            <Box>
              <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.8rem' }}>
                ⚠️ 搜尋結果僅顯示 {order.filteredItemCount}/{totalItems} 件商品
              </Typography>
              <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                此訂單共 {totalItems} 件商品，已收到 {receivedItems}/{totalItems} 件
                {remainingUnreceived > 0 && ` — 還有 ${remainingUnreceived} 件未收到`}
                {remainingUnreceived > 0 && order.filteredItemCount < totalItems && '（部分未顯示在搜尋結果中）'}
              </Typography>
              {order.allItemsReceived && (
                <Typography variant="body2" fontWeight="bold" color="success.main" sx={{ mt: 0.5, fontSize: '0.8rem' }}>
                  ✅ 全部商品已收到，可以出貨!
                </Typography>
              )}
            </Box>
          </Alert>
        );
      })()}

      {/* Gallery Section */}
      {showGallery && (
        <Box sx={{ p: 2, bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0', borderBottom: '1px solid #e2e8f0' }}>
          <ProductImageGallery order={order} />
        </Box>
      )}

      {/* Items Table */}
      <CardContent sx={{ p: 0, '&:last-child': { pb: 0 }, overflow: 'visible' }}>
        <TableContainer sx={{ overflowX: 'auto', overflowY: 'visible' }}>
          <Table size="medium" sx={{ minWidth: 900 }}>
            <TableHead>
              <TableRow sx={styles.tableHeader}>
                <TableCell width="100px" align="center">圖片</TableCell>
                <TableCell width="24%">產品名稱</TableCell>
                <TableCell width="12%">規格</TableCell>
                <TableCell width="60px" align="center">數量</TableCell>
                <TableCell width="12%">標籤</TableCell>
                <TableCell width="14%">備註</TableCell>
                <TableCell width="60px" align="center">收貨</TableCell>
                <TableCell width="70px" align="center">參考</TableCell>
                <TableCell width="10%">SKU</TableCell>
                <TableCell width="60px" align="center">連結</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {order.items.map((item: OrderItem) => {
                const productInfo = productLinks[item.id];
                return (
                  <TableRow
                    key={item.id}
                    sx={styles.tableRow(item.received)}
                  >
                    <TableCell align="center">
                      {item.imageUrl && (
                        <HoverZoomImage
                          src={item.imageUrl}
                          alt={item.productName}
                          width={80}
                          height={80}
                          borderColor="#3b82f6"
                        />
                      )}
                    </TableCell>

                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: '0.95rem',
                            lineHeight: 1.5,
                            color: '#1e293b',
                          }}
                        >
                          {item.productName}
                        </Typography>
                        {item.received && (
                          <CheckCircleIcon sx={{ color: '#22c55e', fontSize: 18, flexShrink: 0 }} />
                        )}
                      </Box>
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2" sx={{ fontSize: '0.9rem', color: '#475569' }}>
                        {item.itemSpec}
                      </Typography>
                    </TableCell>

                    <TableCell align="center">
                      <Box
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          minWidth: 30,
                          height: 30,
                          borderRadius: '8px',
                          bgcolor: item.quantity > 1 ? '#fef3c7' : '#f5f3f0',
                          border: item.quantity > 1 ? '1px solid #fde68a' : '1px solid transparent',
                          fontWeight: 700,
                          fontSize: '0.85rem',
                          color: item.quantity > 1 ? '#92400e' : '#5c5854',
                          fontFamily: '"DM Mono", monospace',
                        }}
                      >
                        {item.quantity}
                      </Box>
                    </TableCell>

                    <TableCell>
                      <ExcelLikeField
                        value={item.tag || ''}
                        onSave={(value) => handleTrackingUpdate(item, 'tag', value)}
                        placeholder="標籤"
                      />
                    </TableCell>

                    <TableCell>
                      <ExcelLikeField
                        value={item.note || ''}
                        onSave={(value) => handleTrackingUpdate(item, 'note', value)}
                        placeholder="備註"
                        multiline
                        rows={1}
                      />
                    </TableCell>

                    <TableCell align="center">
                      <Tooltip
                        title={
                          item.received && item.receivedBy
                            ? `${item.receivedBy} · ${item.receivedAt ? moment(item.receivedAt).format('MM/DD HH:mm') : ''}`
                            : '標記為已收'
                        }
                        arrow
                      >
                        <Checkbox
                          checked={item.received}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setTimeout(() => handleTrackingUpdate(item, 'received', checked), 0);
                          }}
                          color="success"
                          sx={{
                            p: 0.5,
                            transition: 'transform 0.15s ease',
                            '&:hover': { transform: 'scale(1.1)' },
                            '& .MuiSvgIcon-root': {
                              fontSize: 26,
                              borderRadius: '6px',
                            },
                            '&.Mui-checked': {
                              color: '#22c55e',
                            },
                          }}
                        />
                      </Tooltip>
                    </TableCell>

                    <TableCell align="center">
                      {item.referenceImage ? (
                        <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                          <HoverZoomImage
                            src={item.referenceImage}
                            alt="參考"
                            width={48}
                            height={48}
                            borderColor="#22c55e"
                          />
                          <IconButton
                            size="small"
                            onClick={() => handleRemoveReferenceImage(item)}
                            sx={{
                              position: 'absolute',
                              top: -8,
                              right: -8,
                              bgcolor: 'white',
                              boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                              p: 0.25,
                              width: 20,
                              height: 20,
                              '&:hover': { bgcolor: '#fee2e2' },
                            }}
                          >
                            <CloseIcon sx={{ fontSize: 12 }} />
                          </IconButton>
                        </Box>
                      ) : (
                        <IconButton
                          component="label"
                          size="small"
                          sx={{
                            width: 44,
                            height: 44,
                            border: '2px dashed #d1cdc8',
                            borderRadius: '10px',
                            color: '#a39e99',
                            transition: 'all 0.15s ease',
                            '&:hover': {
                              borderColor: '#1a1a1a',
                              color: '#1a1a1a',
                              bgcolor: '#f5f3f0',
                              transform: 'scale(1.05)',
                            },
                          }}
                        >
                          <input hidden type="file" accept="image/*" onChange={(e) => handleReferenceImageUpload(e, item)} />
                          <Typography sx={{ fontSize: 18, fontWeight: 400, lineHeight: 1 }}>+</Typography>
                        </IconButton>
                      )}
                    </TableCell>

                    <TableCell>
                      <Typography
                        variant="caption"
                        sx={{
                          fontFamily: 'monospace',
                          color: '#475569',
                          fontSize: '0.85rem',
                        }}
                      >
                        {item.sku}
                      </Typography>
                    </TableCell>

                    <TableCell align="center">
                      {loadingLinks ? (
                        <CircularProgress size={20} />
                      ) : (() => {
                        const productInfo = productLinks[item.id];

                        // Check if this item has links
                        if (productInfo?.allLinks && productInfo.allLinks.length > 0) {
                          // Use allLinks array - it contains all links in order from the Excel columns
                          const firstLink = productInfo.allLinks[0];  // First link column from Excel
                          const secondLink = productInfo.allLinks.length > 1 ? productInfo.allLinks[1] : null;  // Second link column from Excel

                          return (
                            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                              {/* First link button (left) - from first Link column */}
                              {firstLink && (
                                <Tooltip title="開啟第一個連結">
                                  <IconButton
                                    onClick={() => window.open(firstLink, '_blank', 'noopener,noreferrer')}
                                    sx={{
                                      color: '#3b82f6',
                                      width: 36,
                                      height: 36,
                                      border: '1px solid #bfdbfe',
                                      borderRadius: 1,
                                      '&:hover': {
                                        bgcolor: '#eff6ff',
                                      }
                                    }}
                                  >
                                    <OpenInNewIcon sx={{ fontSize: 18 }} />
                                  </IconButton>
                                </Tooltip>
                              )}

                              {/* Second link button (right) - from second Link column */}
                              {secondLink && (
                                <Tooltip title="開啟第二個連結">
                                  <IconButton
                                    onClick={() => window.open(secondLink, '_blank', 'noopener,noreferrer')}
                                    sx={{
                                      color: '#10b981',
                                      width: 36,
                                      height: 36,
                                      border: '1px solid #a7f3d0',
                                      borderRadius: 1,
                                      '&:hover': {
                                        bgcolor: '#d1fae5',
                                      }
                                    }}
                                  >
                                    <OpenInNewIcon sx={{ fontSize: 18 }} />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Box>
                          );
                        } else if (productInfo?.link) {
                          // Fallback for single link (old data format or products with only one link)
                          return (
                            <Tooltip title="開啟產品頁面">
                              <IconButton
                                onClick={() => window.open(productInfo.link, '_blank', 'noopener,noreferrer')}
                                sx={{
                                  color: '#3b82f6',
                                  width: 36,
                                  height: 36,
                                  border: '1px solid #bfdbfe',
                                  borderRadius: 1,
                                  '&:hover': {
                                    bgcolor: '#eff6ff',
                                  }
                                }}
                              >
                                <OpenInNewIcon sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Tooltip>
                          );
                        }

                        return null;
                      })()}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Summary Footer */}
        {order.isFiltered && (
          <Box sx={{
            py: 1,
            px: 2,
            bgcolor: '#f8fafc',
            borderTop: '1px solid #e2e8f0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}>
            <Typography variant="caption" color="text.secondary">
              訂單摘要: {order.receivedItemsInOrder}/{order.totalItemsInOrder} 項已收
              {order.allItemsReceived && (
                <Chip
                  label="完成"
                  size="small"
                  color="success"
                  sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                />
              )}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
});

// ============================================================================
// MAIN ORDERS PAGE COMPONENT
// ============================================================================

const OrdersPage: React.FC = () => {
  const { shopeeAuthenticated, shopifyAuthenticated, currentUser } = useAuth();
  const { enqueueSnackbar } = useSnackbar();

  // Core state
  const [activeTab, setActiveTab] = useState('shopee');
  const [loading, setLoading] = useState(false);
  const [loadingProductDetails, setLoadingProductDetails] = useState(false);
  const [orders, setOrders] = useState<any[]>([]);
  const [orderDetails, setOrderDetails] = useState<any[]>([]);
  const [allParsedOrders, setAllParsedOrders] = useState<any[]>([]);

  // UI state
  const [statusFilter, setStatusFilter] = useState('All');
  const [sortOrder, setSortOrder] = useState('oldest');
  const [searchFilter, setSearchFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [ordersPerPage] = useState(30);

  // Additional filters
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [isSearchPending, setIsSearchPending] = useState(false);
  const [expandedOrders, setExpandedOrders] = useState<Set<string>>(new Set());
  const [colorFilter, setColorFilter] = useState('All');
  const [sizeFilter, setSizeFilter] = useState('All');
  const [receivedFilter, setReceivedFilter] = useState('All');
  const [tagFilter, setTagFilter] = useState('All');
  // View mode state
  const [viewMode, setViewMode] = useState<'orders' | 'products'>('orders');
  // Auto-compute last ordering cutoff: Wed 20:00 or Sun 20:00 (whichever is most recent)
  const getLastOrderingCutoff = useCallback(() => {
    const now = moment();
    const day = now.day(); // 0=Sun, 3=Wed
    const lastWed = moment().day(day >= 3 ? 3 : 3 - 7).hour(20).minute(0).second(0);
    if (lastWed.isAfter(now)) lastWed.subtract(7, 'days');
    const lastSun = moment().day(day >= 0 ? 0 : 0 - 7).hour(20).minute(0).second(0);
    if (lastSun.isAfter(now)) lastSun.subtract(7, 'days');
    return lastWed.isAfter(lastSun) ? lastWed : lastSun;
  }, []);
  const [groupingAfterDate, setGroupingAfterDate] = useState(() => getLastOrderingCutoff().format('YYYY-MM-DDTHH:mm'));
  const [expandedGroupOrderIds, setExpandedGroupOrderIds] = useState<Set<string>>(new Set());
  const [linkBatchIndex, setLinkBatchIndex] = useState(0);

  // Data state
  const [productMetadata, setProductMetadata] = useState<{ [key: string]: ProductMetadata }>({});

  // Modal and dialog state
  const [shippingDialogData, setShippingDialogData] = useState<any>(null);

  // Socket state
  const [socket, setSocket] = useState<Socket | null>(null);
  const [socketConnected, setSocketConnected] = useState(false);
  const socketRef = useRef<Socket | null>(null);
  const productDetailsFetchedRef = useRef(false);
  const productDetailsCache = useRef<Record<string, any>>({});
  const parsingRef = useRef(false);

  // Refs
  const fetchingRef = useRef(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Track orderDetails count to only re-parse when orders are added/removed
  const prevOrderDetailsCountRef = useRef(0);

  // Debounced search - reduced delay
  const debouncedSearch = useMemo(
    () => _.debounce((searchTerm: string) => {
      startTransition(() => {
        setSearchFilter(searchTerm);
      });
      setIsSearchPending(false);
    }, 300),
    []
  );

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  // Helper functions
  const hasNumbersOnly = useCallback((tag: string) => {
    if (!tag || tag.trim() === '') return false;
    const hasDigit = /\d/.test(tag);
    const onlyNumbersAndSeparators = /^[\d\s\-_.,]*$/.test(tag);
    return hasDigit && onlyNumbersAndSeparators;
  }, []);

  const parseItemSpec = useCallback((itemSpec: string) => {
    if (!itemSpec) return { color: '', size: '' };
    const parts = itemSpec.split(',');
    return { color: parts[0]?.trim() || '', size: parts[1]?.trim() || '' };
  }, []);

  const getProductKey = useCallback((sku: string, productName: string, itemSpec: string) => {
    if (sku && sku.trim()) return sku.trim();
    return `${productName}|${itemSpec || 'default'}`.toLowerCase().trim();
  }, []);

  const decodeHtmlEntities = useCallback((input: string): string => {
    const textArea = document.createElement('textarea');
    textArea.innerHTML = input;
    return textArea.value;
  }, []);

  // Get unique colors and sizes for filters
  const getUniqueColorsAndSizes = useMemo(() => {
    const colors = new Set<string>();
    const sizes = new Set<string>();

    allParsedOrders.forEach(order => {
      order.items.forEach((item: OrderItem) => {
        const { color, size } = parseItemSpec(item.itemSpec);
        if (color) colors.add(color);
        if (size) sizes.add(size);
      });
    });

    return {
      colors: Array.from(colors).sort(),
      sizes: Array.from(sizes).sort()
    };
  }, [allParsedOrders, parseItemSpec]);

  // Fetch product metadata
  const fetchProductMetadata = useCallback(async () => {
    try {
      const response = await api.get('/api/products/metadata');
      const metadata = response.data || [];

      const metadataDict = metadata.reduce((acc: { [key: string]: ProductMetadata }, item: any) => {
        const key = item.product_key;
        if (key) {
          acc[key] = {
            product_key: key,
            tag: item.tag || '',
            reference_image: item.reference_image || undefined,
            referenceImage: item.reference_image || undefined,
            createdAt: item.created_at || new Date().toISOString(),
            lastUpdated: item.last_updated || new Date().toISOString()
          };
        }
        return acc;
      }, {});

      setProductMetadata(metadataDict);
    } catch (error) {
      console.error('獲取產品元數據失敗:', error);
    }
  }, []);

  // Fetch Shopee orders
  const fetchShopeeOrdersComplete = useCallback(async (forceRefresh = false) => {
    if (!shopeeAuthenticated || fetchingRef.current) return;

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    fetchingRef.current = true;
    setLoading(true);

    productDetailsFetchedRef.current = false;
    productDetailsCache.current = {};


    try {

      const ordersResponse = await api.get(`/api/shopee/orders${forceRefresh ? '?refresh=true' : ''}`, {
        signal // Add abort signal to request
      });
      if (signal.aborted) {
        console.log('Shopee fetch aborted');
        return;
      }
      const orderList = ordersResponse.data.response?.order_list || [];

      const uniqueOrderSns = new Set();
      const uniqueOrderList = orderList.filter((order: { order_sn: string }) => {
        if (uniqueOrderSns.has(order.order_sn)) return false;
        uniqueOrderSns.add(order.order_sn);
        return true;
      });

      setOrders(uniqueOrderList);
      //localStorage.setItem('shopeeOrders', JSON.stringify(uniqueOrderList));

      if (uniqueOrderList.length === 0) {
        setOrderDetails([]);
        setAllParsedOrders([]);
        return;
      }

      if (signal.aborted) return;

      const orderSnList = uniqueOrderList.map((o: any) => o.order_sn);

      // Fetch order details and product metadata in parallel
      const [detailsResponse] = await Promise.all([
        api.get(`/api/shopee/orders/details?orderSnList=${orderSnList.join(',')}`, { signal }),
        fetchProductMetadata(),
      ]);
      if (signal.aborted) return;
      const details = detailsResponse.data.response?.order_list || [];

      const uniqueDetailsSns = new Set();
      const uniqueDetails = details.filter((detail: { order_sn: string }) => {
        if (uniqueDetailsSns.has(detail.order_sn)) return false;
        uniqueDetailsSns.add(detail.order_sn);
        return true;
      });

      setOrderDetails(uniqueDetails);
      enqueueSnackbar('訂單已刷新', { variant: 'success' });

    } catch (error: any) {
      if (error?.name === 'AbortError' || error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') return;
      console.error('獲取蝦皮訂單失敗:', error);
      enqueueSnackbar('獲取訂單失敗', { variant: 'error' });
    } finally {
      setLoading(false);
      fetchingRef.current = false;
      abortControllerRef.current = null;
    }
  }, [shopeeAuthenticated, fetchProductMetadata, enqueueSnackbar]);

  // Parse Shopee orders
  const parseShopeeOrders = useCallback(async () => {
    if (!orderDetails || orderDetails.length === 0) {
      setAllParsedOrders([]);
      return;
    }
    if (parsingRef.current) return;
    parsingRef.current = true;

    setLoadingProductDetails(true);
    try {
      const uniqueOrderSns = new Set();
      const uniqueOrderDetails = orderDetails.filter(order => {
        if (uniqueOrderSns.has(order.order_sn)) return false;
        uniqueOrderSns.add(order.order_sn);
        return true;
      });

      // Build product details fetch promise (if needed)
      let item_details: Record<string, any> = productDetailsCache.current;
      let productDetailsFetchPromise: Promise<void> = Promise.resolve();

      if (!productDetailsFetchedRef.current) {
        const allItemIds = new Set<string>();
        uniqueOrderDetails.forEach(order => {
          const items = order.item_list || [];
          items.forEach((item: any) => {
            if (item.item_id) allItemIds.add(item.item_id.toString());
          });
        });

        if (allItemIds.size > 0) {
          const itemIdList = Array.from(allItemIds);
          const batchSize = 50;

          // Fetch all batches in parallel (backend caches, no rate limit concern)
          productDetailsFetchPromise = (async () => {
            try {
              const batchPromises = [];
              for (let i = 0; i < itemIdList.length; i += batchSize) {
                const batchIds = itemIdList.slice(i, i + batchSize);
                batchPromises.push(
                  api.get('/api/shopee/products/details', {
                    params: { itemIds: batchIds.join(',') }
                  }).catch(error => {
                    console.error('獲取批次詳情時出錯:', error);
                    return null;
                  })
                );
              }
              const results = await Promise.all(batchPromises);
              for (const detailsResponse of results) {
                if (detailsResponse?.data?.response?.item_list) {
                  for (const item of detailsResponse.data.response.item_list) {
                    if (item.item_id) {
                      item_details[item.item_id] = item;
                    }
                  }
                }
              }
              productDetailsCache.current = item_details;
              productDetailsFetchedRef.current = true;
            } catch (error) {
              console.error('獲取產品詳情失敗:', error);
            }
          })();
        }
      }

      // Fetch tracking data and product details in parallel
      const [trackingResponse] = await Promise.all([
        api.get('/api/orders/tracking').catch(() => ({ data: [] })),
        productDetailsFetchPromise,
      ]);
      const trackingData: any[] = trackingResponse.data || [];

      // Group orders (rest of your existing code, unchanged)
      const groupedOrders = uniqueOrderDetails.map(order => {
        const items = order.item_list || [];

        const shippingCarrier = order.shipping_carrier ||
          order.checkout_shipping_carrier ||
          (order.package_list && order.package_list[0]?.shipping_carrier) ||
          '標準運送';

        const orderItems = items.map((item: any) => {
          const productKey = getProductKey(item.item_sku, item.item_name, item.model_name);
          const metadata = productMetadata[productKey] || {}; // ✅ Always uses latest metadata

          const tracking = trackingData.find((t) =>
            (t.order_sn || t.orderSn) === order.order_sn.toString() &&
            (t.product_name || t.productName) === item.item_name &&
            (t.item_spec || t.itemSpec) === (item.model_name || '')
          ) || { received: false, missingCount: 0, note: '', tag: '', referenceImage: undefined };

          const allImages: string[] = [];
          if (item.image_info?.image_url) allImages.push(item.image_info.image_url);

          if (item.item_id && item_details[item.item_id]) {
            const detailedItem = item_details[item.item_id];
            if (detailedItem.image?.image_url_list) {
              detailedItem.image.image_url_list.forEach((url: string) => {
                if (!allImages.includes(url)) allImages.push(url);
              });
            }
          }

          return {
            id: `${order.order_sn}_${item.item_id}_${item.model_id}`,
            orderId: order.order_sn,
            productName: item.item_name,
            itemSpec: item.model_name,
            quantity: item.model_quantity_purchased,
            imageUrl: item.image_info?.image_url || '',
            sku: item.model_sku || item.item_sku,
            productKey: productKey,
            original: { ...item, all_images: JSON.stringify(allImages) },
            received: tracking.received || false,
            missingCount: tracking.missingCount || 0,
            note: tracking.note || '',
            tag: metadata.tag || tracking.tag || '', // ✅ metadata.tag will be current
            referenceImage: metadata.reference_image || tracking.referenceImage,
            receivedBy: tracking.receivedBy || undefined,
            receivedAt: tracking.receivedAt || undefined
          };
        });

        const allReceived = orderItems.every((item: { received: any; }) => item.received);
        const anyReceived = orderItems.some((item: { received: any; }) => item.received);

        return {
          orderId: order.order_sn,
          orderNumber: order.order_sn,
          createdAt: moment.unix(order.create_time).format('YYYY-MM-DD HH:mm'),
          createdTimestamp: order.create_time,
          deadline: moment.unix(order.ship_by_date).format('YYYY-MM-DD HH:mm'),
          status: order.order_status,
          receivedStatus: allReceived ? 'complete' : anyReceived ? 'partial' : 'pending',
          allItemsReceived: allReceived,
          totalItemsInOrder: orderItems.length,
          receivedItemsInOrder: orderItems.filter((item: { received: any; }) => item.received).length,
          trackingNumber: order.tracking_number || null,
          shippingCarrier: shippingCarrier,
          totalItems: orderItems.length,
          items: orderItems,
          original: order
        };
      });

      setAllParsedOrders(groupedOrders);

    } catch (error) {
      console.error('解析蝦皮訂單時出錯:', error);
    } finally {
      setLoadingProductDetails(false);
      parsingRef.current = false;
    }
  }, [orderDetails, productMetadata, getProductKey]);

  // Filter and sort orders
  const filteredAndSortedOrders = useMemo(() => {
    if (!allParsedOrders.length) return [];

    let filtered = allParsedOrders;

    // Search filter
    if (searchFilter) {
      const filter = searchFilter.toLowerCase().trim();

      filtered = allParsedOrders.map(order => {
        const isExpanded = expandedOrders.has(order.orderId);
        const orderIdMatches = order.orderId?.toString().toLowerCase().includes(filter);
        const orderNumberMatches = order.orderNumber?.toString().toLowerCase().includes(filter);

        if (orderIdMatches || orderNumberMatches) {
          return {
            ...order,
            originalItemCount: order.items.length,
            filteredItemCount: order.items.length,
            isFiltered: false,
            isExpanded: true,
            allItemsReceived: order.items.every((item: { received: any; }) => item.received)
          };
        }

        const matchingItems = order.items.filter((item: OrderItem) =>
          item.itemSpec?.toLowerCase().includes(filter) ||
          item.productName?.toLowerCase().includes(filter) ||
          item.tag?.toLowerCase().includes(filter) ||
          item.sku?.toLowerCase().includes(filter) ||
          item.note?.toLowerCase().includes(filter)
        );

        if (matchingItems.length > 0) {
          const allItemsReceived = order.items.every((item: { received: any; }) => item.received);
          return {
            ...order,
            items: (isExpanded || allItemsReceived) ? order.items : matchingItems,
            originalItemCount: order.items.length,
            filteredItemCount: matchingItems.length,
            isFiltered: !allItemsReceived,
            isExpanded: isExpanded || allItemsReceived,
            allItemsReceived
          };
        }

        return null;
      }).filter(Boolean);
    } else {
      filtered = allParsedOrders.map(order => ({
        ...order,
        originalItemCount: order.items.length,
        filteredItemCount: order.items.length,
        isFiltered: false,
        allItemsReceived: order.items.every((item: { received: any; }) => item.received)
      }));
    }

    // Received filter
    if (receivedFilter !== 'All') {
      filtered = filtered.map(order => {
        const isExpanded = expandedOrders.has(order.orderId);
        const originalOrder = allParsedOrders.find(o => o.orderId === order.orderId);
        const allOriginalItemsReceived = originalOrder?.items.every((item: { received: any; }) => item.received) ?? false;

        if (receivedFilter === 'ReadyToShip') {
          if (allOriginalItemsReceived || isExpanded) {
            return {
              ...order,
              isFiltered: !isExpanded,
              allItemsReceived: allOriginalItemsReceived
            };
          }
          return null;
        }

        let filteredItems;
        if (receivedFilter === 'Unreceived') {
          filteredItems = order.items.filter((item: { received: any; }) => !item.received);
        } else if (receivedFilter === 'Received') {
          filteredItems = order.items.filter((item: { received: any; }) => item.received);
        } else {
          filteredItems = order.items;
        }

        if (filteredItems.length > 0 || isExpanded || allOriginalItemsReceived) {
          return {
            ...order,
            items: (isExpanded || allOriginalItemsReceived) ? order.items : filteredItems,
            originalItemCount: order.items.length,
            filteredItemCount: (isExpanded || allOriginalItemsReceived) ? order.items.length : filteredItems.length,
            isFiltered: !allOriginalItemsReceived,
            allItemsReceived: allOriginalItemsReceived
          };
        }

        return null;
      }).filter(Boolean);
    }

    // Color filter
    if (colorFilter !== 'All') {
      filtered = filtered.map(order => {
        const matchingItems = order.items.filter((item: OrderItem) => {
          const { color } = parseItemSpec(item.itemSpec);
          return color === colorFilter;
        });

        if (matchingItems.length > 0) {
          const isExpanded = expandedOrders.has(order.orderId);
          return {
            ...order,
            items: isExpanded ? order.items : matchingItems,
            filteredItemCount: matchingItems.length,
            isFiltered: true
          };
        }
        return null;
      }).filter(Boolean);
    }

    // Size filter
    if (sizeFilter !== 'All') {
      filtered = filtered.map(order => {
        const matchingItems = order.items.filter((item: OrderItem) => {
          const { size } = parseItemSpec(item.itemSpec);
          return size === sizeFilter;
        });

        if (matchingItems.length > 0) {
          const isExpanded = expandedOrders.has(order.orderId);
          return {
            ...order,
            items: isExpanded ? order.items : matchingItems,
            filteredItemCount: matchingItems.length,
            isFiltered: true
          };
        }
        return null;
      }).filter(Boolean);
    }

    // Tag filter
    if (tagFilter !== 'All') {
      filtered = filtered.map(order => {
        let matchingItems;

        if (tagFilter === 'NumbersOnly') {
          matchingItems = order.items.filter((item: OrderItem) => hasNumbersOnly(item.tag || ''));
        } else if (tagFilter === 'NoNumbers') {
          matchingItems = order.items.filter((item: OrderItem) => !hasNumbersOnly(item.tag || ''));
        } else if (tagFilter === 'NoTag') {
          matchingItems = order.items.filter((item: OrderItem) => !item.tag || item.tag.trim() === '');
        } else {
          matchingItems = order.items;
        }

        if (matchingItems.length > 0) {
          const isExpanded = expandedOrders.has(order.orderId);
          return {
            ...order,
            items: isExpanded ? order.items : matchingItems,
            filteredItemCount: matchingItems.length,
            isFiltered: true
          };
        }
        return null;
      }).filter(Boolean);
    }

    // Status filter
    if (statusFilter === 'OVERDUE_10') {
      const tenDaysAgo = Math.floor(Date.now() / 1000) - (10 * 24 * 60 * 60);
      filtered = filtered.filter(order =>
        order.createdTimestamp <= tenDaysAgo &&
        order.status !== 'SHIPPED' && order.status !== 'COMPLETED' && order.status !== 'PROCESSED'
      );
    } else if (statusFilter !== 'All') {
      filtered = filtered.filter(order => order.status === statusFilter);
    }

    // Calculate readyToShip and other derived properties
    filtered = filtered.map(order => {
      const originalOrder = allParsedOrders.find(o => o.orderId === order.orderId);
      if (originalOrder) {
        const allReceived = originalOrder.items.every((item: { received: any; }) => item.received);
        const anyReceived = originalOrder.items.some((item: { received: any; }) => item.received);
        const readyToShip = allReceived && (order.status === 'READY_TO_SHIP' || order.status === 'paid');

        return {
          ...order,
          receivedStatus: allReceived ? 'complete' : anyReceived ? 'partial' : 'pending',
          readyToShip,
          allItemsReceived: allReceived,
          totalItemsInOrder: originalOrder.items.length,
          receivedItemsInOrder: originalOrder.items.filter((item: { received: any; }) => item.received).length
        };
      }
      return order;
    });

    // Sort
    if (sortOrder === 'newest') {
      filtered.sort((a, b) => b.createdTimestamp - a.createdTimestamp);
    } else {
      filtered.sort((a, b) => a.createdTimestamp - b.createdTimestamp);
    }

    return filtered;
  }, [allParsedOrders, searchFilter, statusFilter, sortOrder, expandedOrders, colorFilter, sizeFilter, parseItemSpec, receivedFilter, tagFilter, hasNumbersOnly]);

  // Calculate pagination
  const totalPages = Math.ceil(filteredAndSortedOrders.length / ordersPerPage);

  const handlePageChange = useCallback((event: React.ChangeEvent<unknown>, page: number) => {
    startTransition(() => {
      setCurrentPage(page);
    });
  }, []);

  const currentOrders = useMemo(() => {
    const indexOfLastOrder = currentPage * ordersPerPage;
    const indexOfFirstOrder = indexOfLastOrder - ordersPerPage;
    return filteredAndSortedOrders.slice(indexOfFirstOrder, indexOfLastOrder);
  }, [currentPage, ordersPerPage, filteredAndSortedOrders]);

  // Reset page on filter change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchFilter, statusFilter, colorFilter, sizeFilter, sortOrder, tagFilter, receivedFilter]);

  // Fetch product metadata on mount so tags are available for both Shopee and Shopify tabs
  useEffect(() => {
    fetchProductMetadata();
  }, [fetchProductMetadata]);

  // Load initial data
  useEffect(() => {
    const loadInitialData = async () => {
      if (activeTab === 'shopee' && shopeeAuthenticated) {
        // Always fetch fresh data from backend (backend will cache it)
        await fetchShopeeOrdersComplete();
      }
    };

    loadInitialData();
  }, [activeTab, shopeeAuthenticated, fetchShopeeOrdersComplete]);

  // Parse orders only when order count changes (initial load or new/removed orders)
  useEffect(() => {
    const count = orderDetails.length;
    if (count === prevOrderDetailsCountRef.current) return;
    prevOrderDetailsCountRef.current = count;

    if (activeTab === 'shopee' && count > 0) {
      parseShopeeOrders();
    }
  }, [orderDetails.length, activeTab, parseShopeeOrders]);

  // Socket connection - OPTIMIZED
  useEffect(() => {
    // If socket already exists, just join the room and return cleanup
    if (socketRef.current) {
      socketRef.current.emit('join-shop', activeTab);
      return () => { };
    }

    const newSocket = io('https://order-management-system-production.up.railway.app', {
      transports: ['websocket', 'polling'],
      reconnectionAttempts: 5,
      reconnectionDelay: 2000,
      timeout: 10000,
      autoConnect: true,
    });

    socketRef.current = newSocket;
    setSocket(newSocket);

    // ✅ Define handlers as named functions for proper cleanup
    const handleConnect = () => {
      newSocket.emit('join-shop', activeTab);
      setSocketConnected(true);
    };

    const handleDisconnect = () => {
      setSocketConnected(false);
    };

    const handleTrackingUpdated = (data: {
      orderId: string;
      productName: string;
      itemSpec?: string;
      changes: TrackingChanges;
      updatedBy?: string;
      userId?: string;
      timestamp?: string;
      socketId?: string;
    }) => {
      if (!data?.orderId || !data?.productName || !data?.changes) return;
      if (newSocket && data.socketId === newSocket.id) return;

      const updateOrderItems = (orders: any[]) => {
        const orderIndex = orders.findIndex(order => order.orderId === data.orderId);
        if (orderIndex === -1) return orders;

        const order = orders[orderIndex];
        let hasChange = false;

        const updatedItems = order.items.map((item: OrderItem) => {
          if (item.productName === data.productName && (item.itemSpec || '') === (data.itemSpec || '')) {
            hasChange = true;
            return {
              ...item,
              ...(data.changes.received !== undefined && { received: data.changes.received }),
              ...(data.changes.missingCount !== undefined && { missingCount: data.changes.missingCount }),
              ...(data.changes.note !== undefined && { note: data.changes.note }),
              ...(data.changes.tag !== undefined && { tag: data.changes.tag }),
              ...(data.changes.referenceImage !== undefined && { referenceImage: data.changes.referenceImage }),
              ...(data.changes.received === true && data.updatedBy && { receivedBy: data.updatedBy }),
              ...(data.changes.received === true && data.timestamp && { receivedAt: data.timestamp })
            };
          }
          return item;
        });

        if (!hasChange) return orders;

        const allReceived = updatedItems.every((i: { received: any; }) => i.received);
        const anyReceived = updatedItems.some((i: { received: any; }) => i.received);

        const newOrders = [...orders];
        newOrders[orderIndex] = {
          ...order,
          items: updatedItems,
          receivedStatus: allReceived ? 'complete' : anyReceived ? 'partial' : 'pending',
          allItemsReceived: allReceived,
          receivedItemsInOrder: updatedItems.filter((i: { received: any; }) => i.received).length,
          totalItemsInOrder: updatedItems.length
        };

        return newOrders;
      };

      setAllParsedOrders(prev => updateOrderItems(prev));
    };

    const handleOrderShipped = (data: any) => {
      const updateData = {
        status: data.status || 'PROCESSED',
        trackingNumber: data.trackingNumber
      };

      const updateShippedOrder = (orders: any[]) => {
        const index = orders.findIndex(o => o.orderId === data.orderId);
        if (index === -1) return orders;

        const newOrders = [...orders];
        newOrders[index] = { ...orders[index], ...updateData };
        return newOrders;
      };

      setAllParsedOrders(prev => updateShippedOrder(prev));

      // Update raw data without triggering re-parse (optimistic update above handles UI)

      setOrderDetails(prev => {
        const updated = prev.map(o =>
          o.order_sn === data.orderId ? { ...o, order_status: updateData.status, tracking_number: updateData.trackingNumber } : o
        );
        localStorage.setItem('shopeeOrderDetails', JSON.stringify(updated));
        return updated;
      });

    };

    const handleOrdersUpdated = (data: any) => {
      console.log('[Socket] orders-updated received:', data.type, data.timestamp);

      if (data.type === 'new_orders' && data.orders?.length > 0) {
        // New orders need full parse to get product details
        setOrders(prev => {
          const existingSns = new Set(prev.map((o: any) => o.order_sn));
          const newOnes = data.orders.filter((o: any) => !existingSns.has(o.order_sn));
          if (newOnes.length === 0) return prev;
          return [...prev, ...newOnes];
        });

        if (data.orderDetails?.length > 0) {
          setOrderDetails(prev => {
            const existingSns = new Set(prev.map((o: any) => o.order_sn));
            const newOnes = data.orderDetails.filter((o: any) => !existingSns.has(o.order_sn));
            if (newOnes.length === 0) return prev;
            return [...prev, ...newOnes];
          });
        }
      } else if (data.type === 'orders_removed' && data.orderSns?.length > 0) {
        // Remove orders — update allParsedOrders directly, skip re-parse
        const removedSet = new Set(data.orderSns);
        setAllParsedOrders(prev => prev.filter((o: any) => !removedSet.has(o.orderId)));
  
        setOrders(prev => prev.filter((o: any) => !removedSet.has(o.order_sn)));
        setOrderDetails(prev => prev.filter((o: any) => !removedSet.has(o.order_sn)));
  
      }
    };

    // ✅ Register event listeners
    newSocket.on('connect', handleConnect);
    newSocket.on('disconnect', handleDisconnect);
    newSocket.on('tracking-updated', handleTrackingUpdated);
    newSocket.on('order-shipped', handleOrderShipped);
    newSocket.on('orders-updated', handleOrdersUpdated);

    const pingInterval = setInterval(() => {
      if (socketRef.current?.connected) socketRef.current.emit('ping');
    }, 30000);

    // ✅ CRITICAL: Proper cleanup function
    return () => {
      clearInterval(pingInterval);

      // ✅ Remove ALL event listeners
      newSocket.off('connect', handleConnect);
      newSocket.off('disconnect', handleDisconnect);
      newSocket.off('tracking-updated', handleTrackingUpdated);
      newSocket.off('order-shipped', handleOrderShipped);
      newSocket.off('orders-updated', handleOrdersUpdated);

      // Only disconnect on actual unmount, not tab change
      // newSocket.disconnect(); // Uncomment if you want to disconnect on unmount
    };
  }, [activeTab]);

  // Handler functions
  const handleRefresh = useCallback(async () => {
    if (fetchingRef.current) return;
    if (activeTab === 'shopee') await fetchShopeeOrdersComplete(true);
  }, [activeTab, fetchShopeeOrdersComplete]);

  const handleTabChange = useCallback((tab: string) => {
    if (tab === activeTab) return;
    // Abort any in-progress Shopee fetch before switching tabs
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    fetchingRef.current = false;
    setLoading(false);
    setActiveTab(tab);
  }, [activeTab]);

  const handleTrackingUpdate = useCallback((orderItem: OrderItem, field: string, value: any) => {
    const userId = currentUser?.id?.toString() || localStorage.getItem('userId');
    const userName = currentUser?.name || localStorage.getItem('userName') || '未知使用者';
    const currentTime = new Date().toISOString();

    const changes: { [key: string]: any } = { [field]: value };

    if (field === 'received' && value === true) {
      changes.receivedBy = userName;
      changes.receivedAt = currentTime;
    }

    const updateOrdersOptimistically = (prevOrders: any[]) => {
      return prevOrders.map(order => {
        if (order.orderId !== orderItem.orderId) return order;

        const updatedItems = order.items.map((item: OrderItem) => {
          if (item.id !== orderItem.id) return item;

          if (field === 'received') {
            return {
              ...item,
              [field]: value,
              receivedBy: value ? userName : undefined,
              receivedAt: value ? currentTime : undefined
            };
          }

          return { ...item, [field]: value };
        });

        const allReceived = updatedItems.every((i: { received: any; }) => i.received);
        const anyReceived = updatedItems.some((i: { received: any; }) => i.received);

        return {
          ...order,
          items: updatedItems,
          receivedStatus: allReceived ? 'complete' : anyReceived ? 'partial' : 'pending',
          receivedItemsInOrder: updatedItems.filter((i: { received: any; }) => i.received).length,
          allItemsReceived: allReceived
        };
      });
    };

    // Optimistic UI update
    setAllParsedOrders(updateOrdersOptimistically);

    // Handle tag updates
    if (field === 'tag') {
      const productKey = orderItem.productKey || getProductKey(orderItem.sku, orderItem.productName, orderItem.itemSpec);

      api.post('/api/products/metadata', { productKey, tag: value })
        .then(() => {
          setProductMetadata(prev => ({
            ...prev,
            [productKey]: {
              ...prev[productKey],
              product_key: productKey,
              tag: value,
              lastUpdated: new Date().toISOString(),
              createdAt: prev[productKey]?.createdAt || new Date().toISOString(),
            }
          }));
          enqueueSnackbar('標籤已更新', { variant: 'success', autoHideDuration: 2000 });
        })
        .catch(() => {
          enqueueSnackbar('更新標籤失敗', { variant: 'error' });
        });

      if (socket?.connected) {
        socket.emit('tracking-update', {
          shop: activeTab,
          orderId: orderItem.orderId,
          productName: orderItem.productName,
          itemSpec: orderItem.itemSpec || '',
          changes: { tag: value },
          productKey,
          updatedBy: userName,
          userId,
          timestamp: currentTime
        });
      }
    } else {
      const updateData = {
        orderSn: orderItem.orderId.toString(),
        productName: orderItem.productName,
        itemSpec: orderItem.itemSpec || '',
        sku: orderItem.sku || '',
        received: field === 'received' ? value : orderItem.received,
        missingCount: field === 'missingCount' ? value : orderItem.missingCount,
        note: field === 'note' ? value : orderItem.note,
        tag: orderItem.tag,
        userId,
        userName
      };

      api.post('/api/orders/tracking', updateData).catch(() => {
        enqueueSnackbar(`更新 ${field} 失敗`, { variant: 'error' });
      });

      if (socket?.connected) {
        socket.emit('tracking-update', {
          shop: activeTab,
          orderId: orderItem.orderId,
          productName: orderItem.productName,
          itemSpec: orderItem.itemSpec || '',
          changes,
          updatedBy: userName,
          userId,
          timestamp: currentTime
        });
      }

      // Track employee action
      if (field === 'received' && value === true) {
        const trackingKey = `${orderItem.id}_${new Date().toISOString().split('T')[0]}`;
        if (!window.__trackedItems) window.__trackedItems = new Set();

        if (!window.__trackedItems.has(trackingKey)) {
          window.__trackedItems.add(trackingKey);
          import('../services/employeeTrackingService').then(module => {
            module.default.trackItemReceived(orderItem.orderId, orderItem.id, 1, {
              productName: orderItem.productName,
              itemSpec: orderItem.itemSpec,
              sku: orderItem.sku,
              user_id: userId,
              user_name: userName
            });
          }).catch(console.error);
        }
      }

      // Auto-expand if all items received
      if (field === 'received' && value === true) {
        const originalOrder = allParsedOrders.find(o => o.orderId === orderItem.orderId);
        if (originalOrder) {
          const allWillBeReceived = originalOrder.items.every((item: OrderItem) =>
            item.id === orderItem.id || item.received
          );

          if (allWillBeReceived && (searchFilter || colorFilter !== 'All' || sizeFilter !== 'All')) {
            setExpandedOrders(prev => new Set(prev).add(orderItem.orderId));
          }
        }
      }
    }
  }, [currentUser, getProductKey, enqueueSnackbar, socket, activeTab, allParsedOrders, searchFilter, colorFilter, sizeFilter]);

  const handleReferenceImageUpload = useCallback((event: React.ChangeEvent<HTMLInputElement>, item: OrderItem) => {
    const file = event.target.files?.[0];
    if (!file) return;

    enqueueSnackbar('正在處理圖片...', { variant: 'info' });

    const img = new Image();
    const reader = new FileReader();

    reader.onload = (e) => {
      img.src = e.target?.result as string;

      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        const MAX_SIZE = 600;
        let { width, height } = img;

        if (width > height) {
          if (width > MAX_SIZE) { height *= MAX_SIZE / width; width = MAX_SIZE; }
        } else {
          if (height > MAX_SIZE) { width *= MAX_SIZE / height; height = MAX_SIZE; }
        }

        canvas.width = width;
        canvas.height = height;
        ctx?.drawImage(img, 0, 0, width, height);

        const compressedBase64 = canvas.toDataURL('image/jpeg', 0.6);
        const productKey = item.productKey || getProductKey(item.sku, item.productName, item.itemSpec);

        api.post('/api/products/reference-image/base64', { productKey, referenceImage: compressedBase64 })
          .then(() => {
            setProductMetadata(prev => ({
              ...prev,
              [productKey]: { ...prev[productKey], product_key: productKey, reference_image: compressedBase64, lastUpdated: new Date().toISOString() }
            }));

            setAllParsedOrders(prev => prev.map(order => ({
              ...order,
              items: order.items.map((orderItem: OrderItem) => {
                const itemKey = orderItem.productKey || getProductKey(orderItem.sku, orderItem.productName, orderItem.itemSpec);
                return itemKey === productKey ? { ...orderItem, referenceImage: compressedBase64 } : orderItem;
              })
            })));

            enqueueSnackbar('參考圖片已上傳', { variant: 'success' });
          })
          .catch(error => {
            enqueueSnackbar('上傳失敗: ' + (error.response?.data?.error || error.message), { variant: 'error' });
          });
      };
    };

    reader.readAsDataURL(file);
  }, [getProductKey, enqueueSnackbar]);

  const handleRemoveReferenceImage = useCallback((item: OrderItem) => {
    const productKey = item.productKey || getProductKey(item.sku, item.productName, item.itemSpec);

    api.delete(`/api/products/reference-image/${productKey}`)
      .then(() => {
        setProductMetadata(prev => ({
          ...prev,
          [productKey]: { ...prev[productKey], reference_image: undefined, lastUpdated: new Date().toISOString() }
        }));

        setAllParsedOrders(prev => prev.map(order => ({
          ...order,
          items: order.items.map((orderItem: OrderItem) => {
            const itemKey = orderItem.productKey || getProductKey(orderItem.sku, orderItem.productName, orderItem.itemSpec);
            return itemKey === productKey ? { ...orderItem, referenceImage: undefined } : orderItem;
          })
        })));

        enqueueSnackbar('參考圖片已移除', { variant: 'success' });
      })
      .catch(error => {
        enqueueSnackbar('移除失敗: ' + (error.response?.data?.error || error.message), { variant: 'error' });
      });
  }, [getProductKey, enqueueSnackbar]);

  const handleShipOrder = useCallback(async (order: any) => {
    try {
      enqueueSnackbar('正在處理出貨...', { variant: 'info', autoHideDuration: 2000 });

      const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
      const userId = currentUser.id || localStorage.getItem('userId');
      const userName = currentUser.name || localStorage.getItem('userName');

      await api.post(`/api/shopee/shipping/ship/${order.orderId}`, { userId, userName });

      let trackingNumber: string | null = null;
      for (let i = 0; i < 3; i++) {
        try {
          const trackingResponse = await api.get(`/api/shopee/shipping/tracking/${order.orderId}`);
          trackingNumber = trackingResponse.data.response?.tracking_number || null;
          if (trackingNumber) break;
          await new Promise(resolve => setTimeout(resolve, 1000));
        } catch (e) { /* retry */ }
      }

      const updateData = { status: 'PROCESSED', trackingNumber };

      setAllParsedOrders(prev => prev.map(o => o.orderId === order.orderId ? { ...o, ...updateData } : o));

      // Update orderDetails without triggering full re-parse

      setOrderDetails(prev => {
        const updated = prev.map(o =>
          o.order_sn === order.orderId ? { ...o, order_status: 'PROCESSED', tracking_number: trackingNumber } : o
        );
        localStorage.setItem('shopeeOrderDetails', JSON.stringify(updated));
        return updated;
      });


      if (socket?.connected) {
        socket.emit('order-shipped', {
          shop: activeTab,
          orderId: order.orderId,
          status: 'PROCESSED',
          trackingNumber,
          shippedBy: userName,
          userId,
          timestamp: new Date().toISOString()
        });
      }

      enqueueSnackbar(
        trackingNumber ? `已出貨！追蹤號碼：${trackingNumber}` : '已出貨！追蹤號碼稍後顯示',
        { variant: trackingNumber ? 'success' : 'warning', autoHideDuration: 6000 }
      );

    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message;
      enqueueSnackbar(`出貨失敗：${errorMessage}`, { variant: 'error' });
    }
  }, [enqueueSnackbar, socket, activeTab]);

  const handlePrintShippingDocument = useCallback(async (order: any) => {
    try {
      enqueueSnackbar('正在準備出貨單...', { variant: 'info', autoHideDuration: 2000 });

      let trackingNumber = order.trackingNumber;

      if (!trackingNumber) {
        for (let i = 0; i < 3; i++) {
          try {
            const trackingResponse = await api.get(`/api/shopee/shipping/tracking/${order.orderId}`);
            trackingNumber = trackingResponse.data.response?.tracking_number || null;
            if (trackingNumber) break;
            await new Promise(resolve => setTimeout(resolve, 2000));
          } catch (e) { /* retry */ }
        }
      }

      if (!trackingNumber) throw new Error('追蹤號碼尚未準備好');

      const createResponse = await api.post(`/api/shopee/shipping/document/create/${order.orderId}`);
      if (createResponse.data.error) throw new Error(createResponse.data.message || '建立出貨單失敗');

      await new Promise(resolve => setTimeout(resolve, 2000));

      const downloadResponse = await api.post(`/api/shopee/shipping/document/download/${order.orderId}`);
      if (!downloadResponse?.data?.response) throw new Error('無效的單據資料');

      const { type, content } = downloadResponse.data.response;

      if (type === 'pdf') {
        const byteCharacters = atob(content);
        const byteNumbers = new Uint8Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) byteNumbers[i] = byteCharacters.charCodeAt(i);
        const blob = new Blob([byteNumbers], { type: 'application/pdf' });
        const fileURL = URL.createObjectURL(blob);
        window.open(fileURL, '_blank');
        setTimeout(() => URL.revokeObjectURL(fileURL), 100);
      } else if (type === 'html') {
        const blob = new Blob([content], { type: 'text/html' });
        const fileURL = URL.createObjectURL(blob);
        window.open(fileURL, '_blank');
        setTimeout(() => URL.revokeObjectURL(fileURL), 100);
      }

      enqueueSnackbar('出貨單已開啟', { variant: 'success' });
    } catch (error: any) {
      enqueueSnackbar(error.message || '列印出貨單失敗', { variant: 'error' });
    }
  }, [enqueueSnackbar]);

  const handleSkuUpdate = useCallback(async (item: OrderItem, newSku: string) => {
    enqueueSnackbar('SKU 更新僅適用於 Shopify', { variant: 'warning' });
  }, [enqueueSnackbar]);

  const handleSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setIsSearchPending(true);
    debouncedSearch(value);
  }, [debouncedSearch]);

  const clearAllFilters = useCallback(() => {
    if (searchInputRef.current) searchInputRef.current.value = '';
    setSearchFilter('');
    setColorFilter('All');
    setSizeFilter('All');
    setStatusFilter('All');
    setReceivedFilter('All');
    setTagFilter('All');
    setIsSearchPending(false);
    debouncedSearch.cancel();
  }, [debouncedSearch]);

  const clearSearch = useCallback(() => {
    if (searchInputRef.current) searchInputRef.current.value = '';
    setSearchFilter('');
    setIsSearchPending(false);
    debouncedSearch.cancel();
  }, [debouncedSearch]);

  const triggerSearch = useCallback(() => {
    debouncedSearch.cancel();
    setSearchFilter(searchInputRef.current?.value || '');
    setIsSearchPending(false);
  }, [debouncedSearch]);

  const handleFilterClear = useCallback((filter: string) => {
    switch (filter) {
      case 'search': clearSearch(); break;
      case 'status': setStatusFilter('All'); break;
      case 'color': setColorFilter('All'); break;
      case 'size': setSizeFilter('All'); break;
      case 'received': setReceivedFilter('All'); break;
      case 'tag': setTagFilter('All'); break;
    }
  }, [clearSearch]);

  // ============================================================================
  // GROUPED PRODUCT DATA
  // ============================================================================

  const groupedProducts = useMemo(() => {
    if (viewMode !== 'products' || !allParsedOrders.length) return [];

    // Date-time filter
    let timeFilteredOrders = allParsedOrders;
    if (groupingAfterDate) {
      const cutoff = moment(groupingAfterDate).unix();
      if (!isNaN(cutoff)) {
        timeFilteredOrders = allParsedOrders.filter(order => order.createdTimestamp >= cutoff);
      }
    }

    // Status filter — exclude shipped/completed
    if (statusFilter === 'OVERDUE_10') {
      const tenDaysAgo = Math.floor(Date.now() / 1000) - (10 * 24 * 60 * 60);
      timeFilteredOrders = timeFilteredOrders.filter(order =>
        order.createdTimestamp <= tenDaysAgo &&
        order.status !== 'SHIPPED' && order.status !== 'COMPLETED' && order.status !== 'PROCESSED'
      );
    } else if (statusFilter !== 'All') {
      timeFilteredOrders = timeFilteredOrders.filter(order => order.status === statusFilter);
    }

    // Collect all unreceived items
    const productMap = new Map<string, {
      sku: string;
      productName: string;
      imageUrl: string;
      totalQty: number;
      variants: { color: string; size: string; qty: number; orderNumber: string; spec: string }[];
      orderNumbers: Set<string>;
    }>();

    timeFilteredOrders.forEach(order => {
      order.items.forEach((item: OrderItem) => {
        if (item.received) return; // Skip received items

        const key = item.sku || item.productName;
        if (!key) return;

        const { color, size } = parseItemSpec(item.itemSpec);

        if (!productMap.has(key)) {
          productMap.set(key, {
            sku: item.sku || '',
            productName: item.productName,
            imageUrl: item.imageUrl,
            totalQty: 0,
            variants: [],
            orderNumbers: new Set(),
          });
        }

        const group = productMap.get(key)!;
        group.totalQty += item.quantity;
        group.orderNumbers.add(order.orderNumber);
        group.variants.push({
          color: color || '預設',
          size: size || '-',
          qty: item.quantity,
          orderNumber: order.orderNumber,
          spec: item.itemSpec || '',
        });
      });
    });

    // Aggregate variants by spec (color+size)
    return Array.from(productMap.entries()).map(([key, group]) => {
      const specMap = new Map<string, { color: string; size: string; totalQty: number; orders: string[] }>();
      group.variants.forEach(v => {
        const specKey = `${v.color}|${v.size}`;
        if (!specMap.has(specKey)) {
          specMap.set(specKey, { color: v.color, size: v.size, totalQty: 0, orders: [] });
        }
        const s = specMap.get(specKey)!;
        s.totalQty += v.qty;
        if (!s.orders.includes(v.orderNumber)) s.orders.push(v.orderNumber);
      });

      const specs = Array.from(specMap.values());
      // Sort specs: by color first, then by size
      const sizeOrder: { [key: string]: number } = { 'XS': 1, 'S': 2, 'M': 3, 'L': 4, 'XL': 5, '2XL': 6, '3XL': 7, '4XL': 8, '5XL': 9 };
      const getSizeRank = (size: string) => {
        const match = size.match(/^([\dX]*[SML]L?)/i);
        if (match && sizeOrder[match[1].toUpperCase()]) return sizeOrder[match[1].toUpperCase()];
        const num = parseInt(size);
        if (!isNaN(num)) return 100 + num;
        return 500;
      };
      specs.sort((a, b) => {
        const colorCmp = a.color.localeCompare(b.color, 'zh-TW');
        if (colorCmp !== 0) return colorCmp;
        return getSizeRank(a.size) - getSizeRank(b.size);
      });

      return {
        key,
        sku: group.sku,
        productName: group.productName,
        imageUrl: group.imageUrl,
        totalQty: group.totalQty,
        orderCount: group.orderNumbers.size,
        specs,
      };
    }).sort((a, b) => b.totalQty - a.totalQty);
  }, [viewMode, allParsedOrders, groupingAfterDate, statusFilter, parseItemSpec]);

  // Resolve product links for the grouped view
  const groupedProductLinks = useMemo(() => {
    if (viewMode !== 'products') return new Map<string, ProductInfo>();
    const linksMap = new Map<string, ProductInfo>();
    try {
      const storedExcelData = localStorage.getItem('productLinksData');
      if (storedExcelData) {
        const productsList: ExcelProductData[] = JSON.parse(storedExcelData);
        groupedProducts.forEach(group => {
          if (!group.sku) return;
          const match = productsList.find(p => p.id === group.sku);
          if (match) {
            const info: ProductInfo = {
              link: decodeHtmlEntities(match.link || (match.allLinks && match.allLinks[0]) || ''),
              allLinks: match.allLinks?.map((l: string) => decodeHtmlEntities(l)),
            };
            linksMap.set(group.key, info);
          }
        });
      }
    } catch (e) { /* ignore */ }
    return linksMap;
  }, [viewMode, groupedProducts, decodeHtmlEntities]);

  // ============================================================================
  // PRODUCT GROUPING VIEW COMPONENT
  // ============================================================================

  const renderProductGroupingView = () => {
    if (loading) {
      return (
        <Box sx={{ px: 3, py: 4, textAlign: 'center' }}>
          <CircularProgress size={40} />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>載入中...</Typography>
        </Box>
      );
    }

    if (groupedProducts.length === 0) {
      return (
        <Box sx={{ px: 3 }}>
          <Alert severity="info" sx={{ borderRadius: 3, border: '1px solid', borderColor: 'info.light' }}>
            <Typography variant="body2">
              目前沒有需要訂購的商品（所有商品都已收貨，或選擇的時間範圍內沒有訂單）
            </Typography>
          </Alert>
        </Box>
      );
    }

    return (
      <Box sx={{ px: 3, py: 2, overflow: 'auto', height: '100%' }}>
        {/* Summary */}
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', position: 'sticky', top: 0, zIndex: 10, bgcolor: 'white', py: 1, borderBottom: '1px solid #e2e8f0' }}>
          <Typography variant="body1" color="text.secondary">
            共 <strong>{groupedProducts.length}</strong> 種商品需要訂購，
            合計 <strong>{groupedProducts.reduce((sum, g) => sum + g.totalQty, 0)}</strong> 件
          </Typography>
          {(() => {
            const allLinks = groupedProducts
              .map(g => groupedProductLinks.get(g.key))
              .map(info => info?.allLinks?.[0] || info?.link || '')
              .filter(Boolean);
            const remaining = allLinks.length - linkBatchIndex;
            const batchSize = Math.min(10, remaining);
            return (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<OpenInNewIcon />}
                  disabled={remaining <= 0}
                  onClick={() => {
                    const batch = allLinks.slice(linkBatchIndex, linkBatchIndex + 10);
                    (async () => {
                      for (let i = 0; i < batch.length; i++) {
                        window.open(batch[i], '_blank', 'noopener,noreferrer');
                        if (i < batch.length - 1) await new Promise(r => setTimeout(r, 300));
                      }
                    })();
                    setLinkBatchIndex(prev => prev + batch.length);
                    enqueueSnackbar(`開啟第 ${linkBatchIndex + 1}–${linkBatchIndex + batch.length} 個連結`, { variant: 'info' });
                  }}
                  sx={{ textTransform: 'none', borderRadius: 2 }}
                >
                  開啟下{batchSize}個連結 ({linkBatchIndex}/{allLinks.length})
                </Button>
                {linkBatchIndex > 0 && (
                  <Button
                    size="small"
                    onClick={() => setLinkBatchIndex(0)}
                    sx={{ textTransform: 'none', borderRadius: 2, color: '#64748b' }}
                  >
                    重置
                  </Button>
                )}
              </Box>
            );
          })()}
        </Box>

        {/* Product Cards */}
        {groupedProducts.map(group => {
          const linkInfo = groupedProductLinks.get(group.key);
          return (
            <React.Fragment key={group.key}>
              <Card
                elevation={0}
                sx={{
                  mb: 2,
                  borderRadius: 3,
                  border: '1px solid #e2e8f0',
                  bgcolor: 'white',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                  overflow: 'hidden',
                }}
              >
                <CardContent sx={{ p: 2.5 }}>
                  <Box sx={{ display: 'flex', gap: 2.5, alignItems: 'flex-start' }}>
                    {/* Product Image */}
                    {group.imageUrl && (
                      <Box
                        component="img"
                        src={group.imageUrl}
                        alt={group.productName}
                        loading="lazy"
                        sx={{
                          width: 80,
                          height: 80,
                          objectFit: 'contain',
                          borderRadius: 2,
                          border: '1px solid #e2e8f0',
                          flexShrink: 0,
                          cursor: 'pointer',
                        }}
                        onClick={() => window.open(group.imageUrl, '_blank')}
                      />
                    )}

                    {/* Product Info */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0f172a', lineHeight: 1.3 }} noWrap>
                            {group.productName}
                          </Typography>
                          {group.sku && (
                            <Chip
                              label={group.sku}
                              size="small"
                              sx={{ fontFamily: 'monospace', fontSize: '0.8rem', bgcolor: '#f1f5f9', fontWeight: 600 }}
                            />
                          )}
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                          <Chip
                            icon={<ShoppingCartIcon sx={{ fontSize: 16 }} />}
                            label={`${group.totalQty} 件`}
                            color="primary"
                            size="small"
                            sx={{ fontWeight: 700 }}
                          />
                          <Chip
                            label={`${group.orderCount} 筆訂單`}
                            variant="outlined"
                            size="small"
                            sx={{ fontWeight: 500 }}
                          />

                          {/* Factory Link Buttons */}
                          {linkInfo?.allLinks && linkInfo.allLinks.length > 0 ? (
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              {linkInfo.allLinks.map((link, idx) => (
                                <Tooltip key={idx} title={`開啟連結 ${idx + 1}`}>
                                  <IconButton
                                    onClick={() => window.open(link, '_blank', 'noopener,noreferrer')}
                                    sx={{
                                      color: idx === 0 ? '#3b82f6' : '#10b981',
                                      width: 36,
                                      height: 36,
                                      border: '1px solid',
                                      borderColor: idx === 0 ? '#bfdbfe' : '#a7f3d0',
                                      borderRadius: 1,
                                      '&:hover': { bgcolor: idx === 0 ? '#eff6ff' : '#d1fae5' },
                                    }}
                                  >
                                    <OpenInNewIcon sx={{ fontSize: 18 }} />
                                  </IconButton>
                                </Tooltip>
                              ))}
                            </Box>
                          ) : linkInfo?.link ? (
                            <Tooltip title="開啟產品頁面">
                              <IconButton
                                onClick={() => window.open(linkInfo.link, '_blank', 'noopener,noreferrer')}
                                sx={{
                                  color: '#3b82f6',
                                  width: 36,
                                  height: 36,
                                  border: '1px solid #bfdbfe',
                                  borderRadius: 1,
                                  '&:hover': { bgcolor: '#eff6ff' },
                                }}
                              >
                                <OpenInNewIcon sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Tooltip>
                          ) : null}
                        </Box>
                      </Box>

                      {/* Variants Breakdown */}
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1 }}>
                        {group.specs.map((spec, idx) => (
                          <Chip
                            key={idx}
                            onClick={() => {
                              // Toggle expand for all orders in this spec
                              setExpandedGroupOrderIds(prev => {
                                const next = new Set(prev);
                                spec.orders.forEach(o => {
                                  if (next.has(o)) next.delete(o);
                                  else next.add(o);
                                });
                                return next;
                              });
                            }}
                            label={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <span style={{ fontWeight: 600 }}>{spec.color}{spec.size !== '-' ? ` ${spec.size}` : ''}</span>
                                <span style={{ fontWeight: 700, color: '#3b82f6' }}>× {spec.totalQty}</span>
                                <span style={{ fontSize: '0.7rem', color: '#6366f1', marginLeft: 2 }}>
                                  ({spec.orders.map(o => `#${o}`).join(', ')})
                                </span>
                                {spec.orders.some(o => expandedGroupOrderIds.has(o))
                                  ? <ExpandLessIcon sx={{ fontSize: 14, color: '#6366f1', ml: 0.3 }} />
                                  : <ExpandMoreIcon sx={{ fontSize: 14, color: '#94a3b8', ml: 0.3 }} />}
                              </Box>
                            }
                            variant="outlined"
                            size="small"
                            sx={{
                              height: 'auto',
                              py: 0.5,
                              borderRadius: '8px',
                              borderColor: spec.orders.some(o => expandedGroupOrderIds.has(o)) ? '#6366f1' : '#e2e8f0',
                              bgcolor: spec.orders.some(o => expandedGroupOrderIds.has(o)) ? '#f5f3ff' : 'transparent',
                              cursor: 'pointer',
                              '&:hover': { borderColor: '#6366f1', bgcolor: '#f5f3ff' },
                              '& .MuiChip-label': { whiteSpace: 'normal', px: 1 },
                            }}
                          />
                        ))}
                      </Box>
                    </Box>
                  </Box>
                </CardContent>
              </Card>

              {/* Expanded Order Cards */}
              {
                (() => {
                  // Collect all order numbers from this group that are expanded
                  const expandedOrderNums = new Set<string>();
                  group.specs.forEach(spec => {
                    spec.orders.forEach(o => {
                      if (expandedGroupOrderIds.has(o)) expandedOrderNums.add(o);
                    });
                  });
                  if (expandedOrderNums.size === 0) return null;

                  return Array.from(expandedOrderNums).map(orderNum => {
                    const order = allParsedOrders.find(o => o.orderNumber === orderNum);
                    if (!order) return null;
                    return (
                      <Box key={orderNum} sx={{ ml: 4, mb: 2, borderLeft: '3px solid #6366f1', borderRadius: '0 12px 12px 0' }}>
                        <OrderCard
                          order={order}
                          activeTab={activeTab}
                          socket={socket}
                          receivedFilter={receivedFilter}
                          handleShipOrder={handleShipOrder}
                          handlePrintShippingDocument={handlePrintShippingDocument}
                          enqueueSnackbar={enqueueSnackbar}
                          handleTrackingUpdate={handleTrackingUpdate}
                          handleReferenceImageUpload={handleReferenceImageUpload}
                          handleRemoveReferenceImage={handleRemoveReferenceImage}
                          handleSkuUpdate={handleSkuUpdate}
                          setExpandedOrders={setExpandedOrders}
                          setReceivedFilter={setReceivedFilter}
                          decodeHtmlEntities={decodeHtmlEntities}
                        />
                      </Box>
                    );
                  });
                })()}
            </React.Fragment>
          );
        })}
      </Box>
    );
  };

  // Render orders list
  const renderOrdersList = () => {
    if (loading || loadingProductDetails) {
      return (
        <Box sx={{ px: 3 }}>
          {loadingProductDetails && !loading && (
            <Typography sx={{ fontSize: '0.85rem', color: '#8c8580', mb: 1.5, textAlign: 'center' }}>
              正在載入產品詳情...
            </Typography>
          )}
          {[1, 2, 3].map(i => <OrderCardSkeleton key={i} />)}
        </Box>
      );
    }

    if (filteredAndSortedOrders.length === 0) {
      return (
        <Box sx={{ px: 3, py: 6, textAlign: 'center' }}>
          <Box sx={{
            width: 56, height: 56, borderRadius: '16px',
            bgcolor: '#f5f3f0', display: 'flex', alignItems: 'center', justifyContent: 'center',
            mx: 'auto', mb: 2,
          }}>
            <Inventory2Icon sx={{ fontSize: 28, color: '#a39e99' }} />
          </Box>
          <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: '#1a1a1a', mb: 0.5 }}>
            找不到訂單
          </Typography>
          <Typography sx={{ fontSize: '0.85rem', color: '#8c8580' }}>
            {!shopeeAuthenticated && activeTab === 'shopee'
              ? '請先連接蝦皮帳戶以載入訂單'
              : '嘗試調整篩選條件或搜尋關鍵字'}
          </Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%', overflow: 'hidden' }}>
        {/* Pagination Header */}
        <Box sx={{ flexShrink: 0, py: 1.5, px: { xs: 2, md: 3 } }}>
          <Stack spacing={2} direction="row" alignItems="center" justifyContent="space-between">
            <Typography sx={{ fontSize: '0.8rem', color: '#8c8580', fontWeight: 500 }}>
              <strong style={{ color: '#1a1a1a' }}>{(currentPage - 1) * ordersPerPage + 1}-{Math.min(currentPage * ordersPerPage, filteredAndSortedOrders.length)}</strong> / {filteredAndSortedOrders.length} 筆
            </Typography>
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={handlePageChange}
              showFirstButton
              showLastButton
              size="small"
              shape="rounded"
              sx={{
                '& .MuiPaginationItem-root': {
                  fontWeight: 600,
                  fontSize: '0.8rem',
                  borderRadius: '8px',
                  minWidth: 32,
                  height: 32,
                  color: '#5c5854',
                  '&.Mui-selected': {
                    bgcolor: '#1a1a1a',
                    color: '#ffffff',
                    '&:hover': { bgcolor: '#333333' },
                  },
                },
              }}
            />
          </Stack>
        </Box>

        {/* Orders List */}
        <Box sx={{ flexGrow: 1, width: '100%', px: { xs: 2, md: 3 }, pb: 2, overflowY: 'auto' }}>
          {currentOrders.map((order: any) => (
            <Box key={order.orderId} sx={{ mb: 2, contentVisibility: 'auto', containIntrinsicSize: '0 500px' }}>
              <OrderCard
                order={order}
                activeTab={activeTab}
                socket={socket}
                receivedFilter={receivedFilter}
                handleShipOrder={handleShipOrder}
                handlePrintShippingDocument={handlePrintShippingDocument}
                enqueueSnackbar={enqueueSnackbar}
                handleTrackingUpdate={handleTrackingUpdate}
                handleReferenceImageUpload={handleReferenceImageUpload}
                handleRemoveReferenceImage={handleRemoveReferenceImage}
                handleSkuUpdate={handleSkuUpdate}
                setExpandedOrders={setExpandedOrders}
                setReceivedFilter={setReceivedFilter}
                decodeHtmlEntities={decodeHtmlEntities}
              />
            </Box>
          ))}
        </Box>

        {/* Shipping Modal */}
        {shippingDialogData && activeTab === 'shopee' && (
          <ShippingPreparationModal
            open={!!shippingDialogData}
            onClose={() => setShippingDialogData(null)}
            order={shippingDialogData || {}}
            enqueueSnackbar={enqueueSnackbar}
            onShippingPrepared={() => {
              setShippingDialogData(null);
            }}
          />
        )}
      </Box>
    );
  };

  // Shopify tab
  if (activeTab === 'shopify') {
    return (
      <Box sx={{ width: '100%', minHeight: '100vh', bgcolor: '#f8fafc' }}>
        <Box sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h4" sx={styles.headerTitle}>
              訂單管理
            </Typography>

            <Box sx={styles.tabContainer}>
              <Button
                onClick={() => setActiveTab('shopee')}
                disabled={!shopeeAuthenticated}
                sx={styles.tabButton(false)}
              >
                蝦皮
              </Button>
              <Button
                onClick={() => handleTabChange('shopify')}
                disabled={!shopifyAuthenticated}
                sx={styles.tabButton(true)}
              >
                Shopify
              </Button>
            </Box>
          </Box>

          <ShopifyOrdersPage
            socket={socket}
            productMetadata={productMetadata}
            setProductMetadata={setProductMetadata}
          />
        </Box>
      </Box>
    );
  }

  // Main Shopee view
  return (
    <Box sx={styles.pageContainer}>
      {/* Header */}
      <Box sx={styles.header}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h4" sx={styles.headerTitle}>
              訂單管理
            </Typography>

            {/* Connection Status */}
            <Box sx={styles.connectionIndicator(socketConnected)}>
              {socketConnected ? <ConnectedIcon sx={{ fontSize: 14 }} /> : <DisconnectedIcon sx={{ fontSize: 14 }} />}
              {socketConnected ? '已連線' : '離線'}
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
            {/* Tab Toggle */}
            <Box sx={styles.tabContainer}>
              <Button
                onClick={() => setActiveTab('shopee')}
                disabled={!shopeeAuthenticated}
                sx={styles.tabButton(activeTab === 'shopee')}
              >
                蝦皮
              </Button>
              <Button
                onClick={() => handleTabChange('shopify')}
                disabled={!shopifyAuthenticated}
                sx={styles.tabButton(activeTab === 'shopify')}
              >
                Shopify
              </Button>
            </Box>

            {/* View Mode Toggle */}
            <Box sx={styles.tabContainer}>
              <Button
                onClick={() => setViewMode('orders')}
                startIcon={<ViewListIcon sx={{ fontSize: 18 }} />}
                sx={styles.tabButton(viewMode === 'orders')}
              >
                訂單
              </Button>
              <Button
                onClick={() => setViewMode('products')}
                startIcon={<CategoryIcon sx={{ fontSize: 18 }} />}
                sx={styles.tabButton(viewMode === 'products')}
              >
                叫貨
              </Button>
            </Box>

            {/* Refresh Button */}
            <Button
              variant="outlined"
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon sx={{ fontSize: 18 }} />}
              onClick={handleRefresh}
              disabled={loading || fetchingRef.current}
              sx={{
                borderRadius: '10px',
                bgcolor: '#ffffff',
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.85rem',
                borderColor: '#e8e5e1',
                color: '#5c5854',
                px: 2,
                py: 0.75,
                transition: 'all 0.15s ease',
                '&:hover': {
                  borderColor: '#c7c3be',
                  bgcolor: '#fafaf8',
                  transform: 'translateY(-1px)',
                  boxShadow: '0 2px 6px rgba(0,0,0,0.06)',
                },
                '&:active': { transform: 'translateY(0)' },
              }}
            >
              刷新
            </Button>
          </Box>
        </Box>

        <Paper elevation={0} sx={styles.filterPanel}>
          {/* Search field */}
          <Box sx={{ mb: 2 }}>
            <TextField
              fullWidth
              inputRef={searchInputRef}
              defaultValue=""
              placeholder="搜尋標籤、商品名稱、訂單號、規格或客戶名稱..."
              variant="outlined"
              size="small"
              onChange={handleSearchChange}
              onKeyDown={(e) => e.key === 'Enter' && triggerSearch()}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#a39e99', fontSize: 20 }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    {isSearchPending && <CircularProgress size={14} sx={{ mr: 0.5 }} />}
                    {(searchFilter || isSearchPending) && (
                      <IconButton size="small" onClick={clearSearch} sx={{ p: 0.25 }}>
                        <CloseIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    )}
                  </InputAdornment>
                ),
                sx: {
                  borderRadius: '10px',
                  fontSize: '0.9rem',
                  bgcolor: '#ffffff',
                  '& fieldset': { borderColor: '#e8e5e1' },
                  '&:hover fieldset': { borderColor: '#c7c3be !important' },
                  '&.Mui-focused fieldset': { borderColor: '#1a1a1a !important', borderWidth: '1.5px !important' },
                }
              }}
            />
          </Box>

          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} lg={10}>
              <Grid container spacing={1.5}>
                <Grid item xs={6} sm={4} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>狀態</InputLabel>
                    <Select
                      value={statusFilter}
                      label="狀態"
                      onChange={(e) => setStatusFilter(e.target.value)}
                      sx={{ borderRadius: '10px', fontSize: '0.85rem', '& fieldset': { borderColor: '#e8e5e1' } }}
                    >
                      <MenuItem value="All">所有狀態</MenuItem>
                      <MenuItem value="UNPAID">未付款</MenuItem>
                      <MenuItem value="READY_TO_SHIP">待出貨</MenuItem>
                      <MenuItem value="PROCESSED">已處理</MenuItem>
                      <MenuItem value="SHIPPED">已出貨</MenuItem>
                      <MenuItem value="COMPLETED">已完成</MenuItem>
                      <MenuItem value="CANCELLED">已取消</MenuItem>
                      <MenuItem value="OVERDUE_10">超過10天未出貨</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={6} sm={4} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>收貨狀態</InputLabel>
                    <Select
                      value={receivedFilter}
                      label="收貨狀態"
                      onChange={(e) => setReceivedFilter(e.target.value)}
                      sx={{ borderRadius: '10px', fontSize: '0.85rem', '& fieldset': { borderColor: '#e8e5e1' } }}
                    >
                      <MenuItem value="All">全部</MenuItem>
                      <MenuItem value="Unreceived">未收貨</MenuItem>
                      <MenuItem value="Received">已收貨</MenuItem>
                      <MenuItem value="ReadyToShip">可出貨</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={6} sm={4} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>顏色</InputLabel>
                    <Select
                      value={colorFilter}
                      label="顏色"
                      onChange={(e) => setColorFilter(e.target.value)}
                      sx={{ borderRadius: '10px', fontSize: '0.85rem', '& fieldset': { borderColor: '#e8e5e1' } }}
                    >
                      <MenuItem value="All">所有顏色</MenuItem>
                      {getUniqueColorsAndSizes.colors.map(color => (
                        <MenuItem key={color} value={color}>{color}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={6} sm={4} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>尺寸</InputLabel>
                    <Select
                      value={sizeFilter}
                      label="尺寸"
                      onChange={(e) => setSizeFilter(e.target.value)}
                      sx={{ borderRadius: '10px', fontSize: '0.85rem', '& fieldset': { borderColor: '#e8e5e1' } }}
                    >
                      <MenuItem value="All">所有尺寸</MenuItem>
                      {getUniqueColorsAndSizes.sizes.map(size => (
                        <MenuItem key={size} value={size}>{size}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={6} sm={4} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>標籤</InputLabel>
                    <Select
                      value={tagFilter}
                      label="標籤"
                      onChange={(e) => setTagFilter(e.target.value)}
                      sx={{ borderRadius: '10px', fontSize: '0.85rem', '& fieldset': { borderColor: '#e8e5e1' } }}
                    >
                      <MenuItem value="All">所有標籤</MenuItem>
                      <MenuItem value="NumbersOnly">僅數字</MenuItem>
                      <MenuItem value="NoNumbers">非數字</MenuItem>
                      <MenuItem value="NoTag">無標籤</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={6} sm={4} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>排序</InputLabel>
                    <Select
                      value={sortOrder}
                      label="排序"
                      onChange={(e) => setSortOrder(e.target.value)}
                      sx={{ borderRadius: '10px', fontSize: '0.85rem', '& fieldset': { borderColor: '#e8e5e1' } }}
                    >
                      <MenuItem value="newest">最新優先</MenuItem>
                      <MenuItem value="oldest">最舊優先</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Grid>

          </Grid>

          {/* Active Filters */}
          <FilterSummary
            searchFilter={searchFilter}
            statusFilter={statusFilter}
            colorFilter={colorFilter}
            sizeFilter={sizeFilter}
            receivedFilter={receivedFilter}
            tagFilter={tagFilter}
            onClear={handleFilterClear}
            onClearAll={clearAllFilters}
          />
        </Paper>

        {/* Time Period Selector for Product Grouping View */}
        {viewMode === 'products' && (
          <Box sx={{
            display: 'flex',
            gap: 1.5,
            mb: 2,
            p: 2,
            bgcolor: 'white',
            borderRadius: 3,
            border: '1px solid #e2e8f0',
            alignItems: 'center',
            boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
          }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
              📅 顯示此時間之後的訂單：
            </Typography>
            <TextField
              type="datetime-local"
              value={groupingAfterDate}
              onChange={(e) => setGroupingAfterDate(e.target.value)}
              size="small"
              InputLabelProps={{ shrink: true }}
              sx={{
                minWidth: 220,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  bgcolor: '#f8fafc',
                },
              }}
            />
            {groupingAfterDate && (
              <Button
                size="small"
                variant="text"
                onClick={() => setGroupingAfterDate('')}
                sx={{ textTransform: 'none', color: '#64748b', minWidth: 'auto' }}
              >
                清除
              </Button>
            )}
            <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
              {groupingAfterDate
                ? `顯示 ${moment(groupingAfterDate).format('YYYY/MM/DD HH:mm')} 之後的訂單`
                : '顯示全部訂單'}
            </Typography>
          </Box>
        )}

        {/* Dashboard Summary Stats */}
        {(() => {
          const pendingCount = allParsedOrders.filter(o => o.receivedStatus === 'pending' && !o.allItemsReceived).length;
          const partialCount = allParsedOrders.filter(o => o.receivedStatus === 'partial').length;
          const readyCount = allParsedOrders.filter(o => o.allItemsReceived && o.status !== 'PROCESSED' && o.status !== 'SHIPPED' && o.status !== 'COMPLETED').length;
          const shippedCount = allParsedOrders.filter(o => o.status === 'PROCESSED' || o.status === 'SHIPPED' || o.status === 'COMPLETED').length;
          const totalCount = allParsedOrders.length;
          const statItems = [
            { label: '待處理', count: pendingCount, color: '#ef4444', bg: '#fef2f2', border: '#fecaca' },
            { label: '部分收到', count: partialCount, color: '#d97706', bg: '#fffbeb', border: '#fde68a' },
            { label: '可出貨', count: readyCount, color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0' },
            { label: '已出貨', count: shippedCount, color: '#2563eb', bg: '#eff6ff', border: '#bfdbfe' },
          ];
          return (
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' },
              gap: 1.5, mb: 0, mt: 2,
            }}>
              {statItems.map(s => (
                <Box key={s.label} sx={{
                  px: 2, py: 1.5,
                  bgcolor: s.bg,
                  borderRadius: '12px',
                  border: `1px solid ${s.border}`,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  transition: 'transform 0.15s ease',
                  '&:hover': { transform: 'translateY(-1px)' },
                }}>
                  <Typography sx={{ fontSize: '1.5rem', fontWeight: 800, color: s.color, lineHeight: 1, fontFamily: '"DM Sans", sans-serif' }}>
                    {s.count}
                  </Typography>
                  <Box>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: s.color, lineHeight: 1.2, letterSpacing: '0.02em' }}>
                      {s.label}
                    </Typography>
                    <Typography sx={{ fontSize: '0.65rem', color: alpha(s.color, 0.6), lineHeight: 1.2, mt: 0.25 }}>
                      {totalCount > 0 ? `${Math.round((s.count / totalCount) * 100)}%` : '0%'}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          );
        })()}
      </Box>

      {/* Orders List or Product Grouping View */}
      <Box sx={{ flexGrow: 1, overflow: 'hidden', position: 'relative' }}>
        {viewMode === 'products' ? renderProductGroupingView() : renderOrdersList()}
      </Box>
    </Box>
  );
};

export default OrdersPage;